import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { policiesApi, Policy, PolicyCreateData } from '../services/policiesApi';
import { normalizePolicyActionsForSave } from '../utils/policyActionPayload';
import { operatorOverridesApi } from '../services/operatorOverridesApi';
import { zonesApi, Zone } from '../services/zonesApi';
import { camerasApi, Camera } from '../services/camerasApi';
import { linesApi, Line } from '../services/linesApi';
import { api, ensureUiScope, endpoints } from '../services/api';
import { sitesApi, type SiteComplexity } from '../services/sitesApi';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';
import { canAccessWarehouseSurface, canDeployPolicies, canManagePolicies } from '../auth/access';
import { scheduleDeferredWork } from '../utils/deferredWork';
import PoliciesIntroSection from '../components/policies/PoliciesIntroSection';
import PolicyTable, { type PolicyTableRowModel } from '../components/policies/PolicyTable';
import PoliciesPageContent from '../components/policies/PoliciesPageContent';
import { buildPolicyTroubleshootingView, formatDeployReasonCode } from '../utils/policyTroubleshooting';
import { formatPolicyApiError } from '../utils/policyDeployErrors';
import { useStartMeasuring } from '../domain/safetyCases/useStartMeasuring';
import {
  PACK_BY_KEY,
  getPackKeyForAlias,
  getPolicyAliasDefinition,
  getPolicyLabelFromAliasOrType,
  getPolicySubjectFromEntityTypes,
  inferPolicyAlias,
  readPolicyUiContext,
  resolvePackAndAlias,
  withPolicyReportContext,
  PACK_DISPLAY_ORDER,
  POLICY_PACKS,
  type PackKey,
  type PolicySubject,
} from '../domain/policies/policyPacks';
import { presentPolicy, type PresentedPolicy } from '../domain/policies/policyPresentation';
import {
  DEFAULT_PPE_REQUIRED_ITEMS,
  SEVERITIES,
  DAYS,
  isBehaviorPolicyType,
  readBehaviorRuleUiState,
  applyBehaviorRuleUiState,
  readOccupancyRuleUiState,
  applyOccupancyRuleUiState,
  readWorkerDownRuleUiState,
  applyWorkerDownRuleUiState,
  readForkliftProximityRuleUiState,
  applyForkliftProximityRuleUiState,
  readPedestrianNearMovingForkliftRuleUiState,
  applyPedestrianNearMovingForkliftRuleUiState,
  readForkliftSpeedingRuleUiState,
  applyForkliftSpeedingRuleUiState,
  readZoneIntrusionRuleUiState,
  applyZoneIntrusionRuleUiState,
  readLineCrossingRuleUiState,
  applyLineCrossingRuleUiState,
  readRegionTransitionRuleUiState,
  applyRegionTransitionRuleUiState,
  COMPOSITE_POLICY_TYPE,
} from '../components/policies/policyConfigPresets';
import {
  CompositeEditorSwitch,
  blockCompositeRuntime,
  openCompositePolicy,
  saveCompositePolicy,
} from '../components/policies/CompositeAlertEditor';
import PackChooserStep from '../components/policies/PackChooserStep';
import PackPolicyChooserStep from '../components/policies/PackPolicyChooserStep';
import PolicyConfigureStep from '../components/policies/PolicyConfigureStep';
import PolicyReviewStep from '../components/policies/PolicyReviewStep';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../utils/configVocabulary';

function getPolicyTypeLabel(
  policyOrType: string | Pick<Policy, 'policy_type' | 'scope'>,
  zonesById?: ReadonlyMap<string, Zone>,
): string {
  const policyType = typeof policyOrType === 'string' ? policyOrType : String(policyOrType.policy_type || '').trim();
  if (policyType === COMPOSITE_POLICY_TYPE) return 'Composite Alert';
  const scope = typeof policyOrType === 'string' ? undefined : policyOrType.scope;
  const subject = getPolicySubjectFromEntityTypes(scope?.entity_types);
  const alias = inferPolicyAlias({
    policyType,
    subject,
    zoneIds: scope?.zone_ids,
    zonesById,
  });
  return getPolicyLabelFromAliasOrType({ alias, policyType: policyType as any });
}

function normalizePackKey(value: string | null | undefined): PackKey | null {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  return PACK_BY_KEY[normalized as PackKey] ? (normalized as PackKey) : null;
}

type PolicyStatusFilter = 'all' | 'active' | 'inactive' | 'draft' | 'invalid' | 'needs_zone' | 'pending_deploy';
type PolicyPackFilter = 'all' | PackKey;

type PolicyManagementRow = PolicyTableRowModel & {
  packKey: PackKey;
  isActive: boolean;
  isInactive: boolean;
  isDraft: boolean;
  isInvalid: boolean;
  needsZone: boolean;
  pendingDeploy: boolean;
};

function normalizePatternForSave(payload: PolicyCreateData, editing: boolean): void {
  delete (payload as any).escalation;
  const pattern = payload.pattern;
  if (pattern?.enabled === true) return;
  if (editing) payload.pattern = { enabled: false };
  else delete payload.pattern;
}

function normalizeImmediateForSave(payload: PolicyCreateData): void {
  payload.alert = {
    ...(payload.alert || {}),
    severity: payload.alert?.severity || 'medium',
    enabled: payload.alert?.enabled !== false,
  };
}

type PolicyPackSummary = {
  key: PackKey;
  label: string;
  valueProp: string;
  count: number;
  activeCount: number;
  inactiveCount: number;
};

const listDevicesWithPolicies = (policies: Policy[]): Array<{ deviceId: string; count: number }> => {
  const counts = new Map<string, number>();
  for (const policy of policies) {
    const deviceId = String(policy.target?.edge_device_id || policy.edge_device_id || policy.scope?.edge_device_id || '').trim();
    if (!deviceId) continue;
    counts.set(deviceId, (counts.get(deviceId) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([deviceId, count]) => ({ deviceId, count }))
    .sort((left, right) => left.deviceId.localeCompare(right.deviceId));
};

const deleteAllPoliciesForDevice = async ({
  deleteAllDeviceId,
  devicesWithPolicies,
  siteId,
  setDeleteAllDeviceSaving,
  setSnackbar,
  setDeleteAllDeviceOpen,
  setDeleteAllDeviceId,
  fetchPolicies,
}: {
  deleteAllDeviceId: string;
  devicesWithPolicies: Array<{ deviceId: string; count: number }>;
  siteId: string;
  setDeleteAllDeviceSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbar: React.Dispatch<React.SetStateAction<any>>;
  setDeleteAllDeviceOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteAllDeviceId: React.Dispatch<React.SetStateAction<string>>;
  fetchPolicies: () => void;
}) => {
  if (!deleteAllDeviceId) return;
  const entry = devicesWithPolicies.find((device) => device.deviceId === deleteAllDeviceId);
  const count = entry?.count ?? 0;
  if (!window.confirm(`Delete all ${count} policy(ies) on device "${deleteAllDeviceId}"? This cannot be undone.`)) return;
  setDeleteAllDeviceSaving(true);
  try {
    const res = await policiesApi.deleteAllPoliciesForDevice(siteId, deleteAllDeviceId);
    setSnackbar({ open: true, message: res.message || `Deleted ${res.deleted_count} policies`, severity: 'success' });
    setDeleteAllDeviceOpen(false);
    setDeleteAllDeviceId('');
    fetchPolicies();
  } catch (err: any) {
    setSnackbar({ open: true, message: err?.response?.data?.error || err?.message || 'Failed to delete policies', severity: 'error' });
  } finally {
    setDeleteAllDeviceSaving(false);
  }
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const ts = Date.parse(value);
  if (!Number.isFinite(ts)) return value;
  return new Date(ts).toLocaleString();
};

const looksTechnicalPolicyLabel = (value?: string | null): boolean => {
  const name = String(value || '').trim();
  if (!name) return true;
  if (/epic\d+/i.test(name)) return true;
  if (/[0-9]{8,}/.test(name)) return true;
  if (/[0-9a-f]{8}-[0-9a-f]{4}/i.test(name)) return true;
  if (name.includes('_')) return true;
  return false;
};

const titleCaseWords = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const cleanTechnicalPolicyLabel = (value?: string | null): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return titleCaseWords(
    raw
      .replace(/epic\d+/gi, ' ')
      .replace(/[0-9a-f]{8}-[0-9a-f-]{8,}/gi, ' ')
      .replace(/[_-]?\d{8,}/g, ' ')
      .replace(/\s+/g, ' ')
      .trim(),
  );
};

const derivePolicyDisplayName = (policy: Policy, presented: PresentedPolicy | null): string => {
  const explicit = String(policy.name || '').trim();
  if (explicit && !looksTechnicalPolicyLabel(explicit)) return explicit;
  const cleanedExplicit = cleanTechnicalPolicyLabel(explicit);
  if (cleanedExplicit) return cleanedExplicit;
  const presentedLabel = String(presented?.policyLabel || '').trim();
  if (presentedLabel) return presentedLabel;
  const cleanedId = cleanTechnicalPolicyLabel(policy.policy_id);
  return cleanedId || policy.policy_id || 'Unnamed policy';
};

const derivePolicyStatusLabel = (policy: Policy, view: ReturnType<typeof buildPolicyTroubleshootingView>): string => {
  if (['missing_target', 'orphaned_camera', 'lint_blocked', 'zone_issue'].includes(view.state)) return CONFIG_STATE_LABELS.blocked;
  if (policy.status === 'draft') return CONFIG_STATE_LABELS.draft;
  if (policy.status !== 'active' || policy.enabled === false) return CONFIG_STATE_LABELS.draft;
  return CONFIG_STATE_LABELS.live;
};

const derivePolicyDeploymentLabel = (policy: Policy, view: ReturnType<typeof buildPolicyTroubleshootingView>): string => {
  const deploymentReason = String(policy.deployment_reason || '').trim().toLowerCase();
  if (view.state === 'healthy') {
    if (deploymentReason.includes('pending deploy') || deploymentReason.includes('newer changes pending')) {
      return CONFIG_STATE_LABELS.needsAction;
    }
    return CONFIG_STATE_LABELS.deployed;
  }
  if (view.state === 'pending_deploy') return CONFIG_STATE_LABELS.needsAction;
  if (view.state === 'deploying') return CONFIG_STATE_LABELS.deploying;
  if (view.state === 'deployment_failed' || view.state === 'deployment_timeout') return CONFIG_STATE_LABELS.blocked;
  if (policy.status !== 'active' || policy.enabled === false) return CONFIG_STATE_LABELS.draft;
  return view.deploymentMeta.label || CONFIG_STATE_LABELS.unknown;
};

const deriveScopeIndicator = (policy: Policy, view: ReturnType<typeof buildPolicyTroubleshootingView>): string => {
  const zoneIds = Array.isArray(policy.scope?.zone_ids) ? policy.scope.zone_ids : [];
  const geometryMode = String(policy.scope?.geometry_mode || '').trim().toLowerCase();
  const lineId = String((policy.config as any)?.line_id || (policy.config as any)?.line?.line_id || '').trim();
  if (policy.policy_type === 'line_crossing' && !lineId) return CONFIG_SCOPE_LABELS.needsLineScope;
  if (policy.policy_type !== 'line_crossing' && zoneIds.length === 0 && geometryMode !== 'whole_view') return CONFIG_SCOPE_LABELS.needsZoneScope;
  if (geometryMode === 'whole_view') return CONFIG_SCOPE_LABELS.wholeArea;
  if (view.state === 'zone_issue') return CONFIG_SCOPE_LABELS.needsZoneScope;
  return '';
};

const resolvePolicyEditorSelection = ({
  policyType,
  subject,
  zoneIds,
  zonesById,
  uiContext,
}: {
  policyType: PolicyCreateData['policy_type'];
  subject: PolicySubject;
  zoneIds?: unknown;
  zonesById?: ReadonlyMap<string, Zone>;
  uiContext?: ReturnType<typeof readPolicyUiContext>;
}): { packKey: PackKey; optionId: string } => {
  const selection = resolvePackAndAlias({
    policyType,
    subject,
    zoneIds,
    zonesById,
    uiContext,
  });
  return {
    optionId: selection.alias,
    packKey: selection.packKey,
  };
};

const hasScheduleWindows = (windows: any): boolean => {
  if (!windows || typeof windows !== 'object') return false;
  return Object.values(windows).some((list) => Array.isArray(list) && list.length > 0);
};

const isTimeBasedSchedule = (schedule: any): boolean => {
  if (!schedule) return false;
  if (schedule.enabled === false) return false;
  const hasLegacy =
    (Array.isArray(schedule.days) && schedule.days.length > 0) ||
    Boolean(schedule.start_time) ||
    Boolean(schedule.end_time);
  const hasExceptions = Array.isArray(schedule.exceptions) && schedule.exceptions.length > 0;
  return schedule.enabled === true || hasScheduleWindows(schedule.windows) || hasLegacy || hasExceptions;
};

const PAGE_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

type PoliciesPackSectionsProps = {
  rows: PolicyManagementRow[];
  loading: boolean;
  selectedPolicyId: string | null;
  onSelectPolicy: (row: PolicyManagementRow) => void;
  onOpenActions: (event: React.MouseEvent<HTMLElement>, row: PolicyManagementRow) => void;
};

const PoliciesPackSections: React.FC<PoliciesPackSectionsProps> = ({
  rows,
  loading,
  selectedPolicyId,
  onSelectPolicy,
  onOpenActions,
}) => (
  <PolicyTable
    rows={rows}
    loading={loading}
    selectedPolicyId={selectedPolicyId}
    onSelectRow={(row) => onSelectPolicy(row as PolicyManagementRow)}
    onOpenActions={(event, row) => onOpenActions(event, row as PolicyManagementRow)}
  />
);

type PoliciesEditorBodyProps = {
  editorStep: number;
  selectedPolicyPack: PackKey | null;
  selectedPolicyAlias: string;
  setSelectedPolicyPack: React.Dispatch<React.SetStateAction<PackKey | null>>;
  setSelectedPolicyAlias: React.Dispatch<React.SetStateAction<string>>;
  setEditorStep: React.Dispatch<React.SetStateAction<number>>;
  applyPolicyAliasSelection: (optionId: string) => void;
  selectedPolicyOption: ReturnType<typeof getPolicyAliasDefinition>;
  selectedPolicySubject: PolicySubject;
  formData: Partial<PolicyCreateData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<PolicyCreateData>>>;
  selectedCameraId: string;
  selectedCameraProfileId: '' | 'A' | 'B' | 'C' | 'D';
  setSelectedCameraId: React.Dispatch<React.SetStateAction<string>>;
  selectedEdgeDeviceId: string;
  setSelectedEdgeDeviceId: React.Dispatch<React.SetStateAction<string>>;
  cameras: Camera[];
  selectedZoneIds: string[];
  setSelectedZoneIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLineId: string;
  setSelectedLineId: React.Dispatch<React.SetStateAction<string>>;
  lineDirection: 'both' | 'a_to_b' | 'b_to_a';
  setLineDirection: React.Dispatch<React.SetStateAction<'both' | 'a_to_b' | 'b_to_a'>>;
  availableZones: Zone[];
  availableLines: Line[];
  openZonePreview: (zone: Zone) => void;
  scopeReady: boolean;
  scopeRequirements: string[];
  editorTab: 'scope' | 'hours' | 'actions';
  setEditorTab: React.Dispatch<React.SetStateAction<'scope' | 'hours' | 'actions'>>;
  weekGrid: Record<string, boolean[]>;
  setWeekGrid: React.Dispatch<React.SetStateAction<Record<string, boolean[]>>>;
  initDefaultGrid: () => Record<string, boolean[]>;
  gridToWindows: (g: Record<string, boolean[]>) => Record<string, Array<{ start: string; end: string }>>;
  mode: string;
  saving: boolean;
  editingPolicy: Policy | null;
  clockStatusByDevice: Record<string, boolean | null>;
  deriveDeviceIdsForPolicy: (policy: Partial<Policy>, opts?: any) => string[];
  navigate: (to: string) => void;
  setSnackbar: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning'; autoHideDuration?: number }>>;
  selectedPackGroup: typeof PACK_BY_KEY[PackKey] | null;
  allowsWholeViewScope: boolean;
  postCreateAction: 'draft' | 'activate' | 'activate_deploy';
  setPostCreateAction: React.Dispatch<React.SetStateAction<'draft' | 'activate' | 'activate_deploy'>>;
  sitePolicies?: Policy[];
};

const PoliciesEditorBody: React.FC<PoliciesEditorBodyProps> = ({
  editorStep,
  selectedPolicyPack,
  selectedPolicyAlias,
  setSelectedPolicyPack,
  setSelectedPolicyAlias,
  setEditorStep,
  applyPolicyAliasSelection,
  selectedPolicyOption,
  selectedPolicySubject,
  formData,
  setFormData,
  selectedCameraId,
  selectedCameraProfileId,
  setSelectedCameraId,
  selectedEdgeDeviceId,
  setSelectedEdgeDeviceId,
  cameras,
  selectedZoneIds,
  setSelectedZoneIds,
  selectedLineId,
  setSelectedLineId,
  lineDirection,
  setLineDirection,
  availableZones,
  availableLines,
  openZonePreview,
  scopeReady,
  scopeRequirements,
  editorTab,
  setEditorTab,
  weekGrid,
  setWeekGrid,
  initDefaultGrid,
  gridToWindows,
  mode,
  saving,
  editingPolicy,
  clockStatusByDevice,
  deriveDeviceIdsForPolicy,
  navigate,
  setSnackbar,
  selectedPackGroup,
  allowsWholeViewScope,
  postCreateAction,
  setPostCreateAction,
  sitePolicies = [],
}) => (
  <CompositeEditorSwitch
    formData={formData}
    setFormData={setFormData}
    editorStep={editorStep}
    setEditorStep={setEditorStep}
    setPostCreateAction={setPostCreateAction}
    sourcePolicies={sitePolicies}
    cameras={cameras}
  >
    {editorStep === 0 && (
      <PackChooserStep
        selectedPackKey={selectedPolicyPack}
        onSelectPack={(key) => {
          setSelectedPolicyPack(key);
          setSelectedPolicyAlias('');
        }}
      />
    )}

    {editorStep === 1 && selectedPolicyPack && (
      <PackPolicyChooserStep
        selectedPackKey={selectedPolicyPack}
        selectedPolicyAlias={selectedPolicyAlias}
        onSelectPolicy={applyPolicyAliasSelection}
        onChangeBack={() => {
          setSelectedPolicyPack(null);
          setSelectedPolicyAlias('');
          setEditorStep(0);
        }}
      />
    )}

    {editorStep === 2 && (
      <PolicyConfigureStep
        selectedPolicyOption={selectedPolicyOption}
        selectedPolicySubject={selectedPolicySubject}
        formData={formData}
        setFormData={setFormData}
        selectedCameraId={selectedCameraId}
        selectedCameraProfileId={selectedCameraProfileId}
        setSelectedCameraId={setSelectedCameraId}
        selectedEdgeDeviceId={selectedEdgeDeviceId}
        setSelectedEdgeDeviceId={setSelectedEdgeDeviceId}
        cameras={cameras}
        selectedZoneIds={selectedZoneIds}
        setSelectedZoneIds={setSelectedZoneIds}
        selectedLineId={selectedLineId}
        setSelectedLineId={setSelectedLineId}
        lineDirection={lineDirection}
        setLineDirection={setLineDirection}
        availableZones={availableZones}
        availableLines={availableLines}
        openZonePreview={openZonePreview}
        scopeReady={scopeReady}
        scopeRequirements={scopeRequirements}
        editorTab={editorTab}
        setEditorTab={setEditorTab}
        weekGrid={weekGrid}
        setWeekGrid={setWeekGrid}
        initDefaultGrid={initDefaultGrid}
        gridToWindows={gridToWindows}
        isTimeBasedSchedule={isTimeBasedSchedule}
        mode={mode}
        saving={saving}
        editingPolicy={editingPolicy}
        clockStatusByDevice={clockStatusByDevice}
        deriveDeviceIdsForPolicy={deriveDeviceIdsForPolicy}
        navigate={navigate}
        setSnackbar={setSnackbar}
      />
    )}

    {editorStep === 3 && (
      <PolicyReviewStep
        selectedPackGroup={selectedPackGroup}
        selectedPolicyOption={selectedPolicyOption}
        selectedPolicySubject={selectedPolicySubject}
        formData={formData}
        setFormData={setFormData}
        selectedCameraId={selectedCameraId}
        selectedCameraProfileId={selectedCameraProfileId}
        selectedZoneIds={selectedZoneIds}
        selectedLineId={selectedLineId}
        allowsWholeViewScope={allowsWholeViewScope}
        scopeReady={scopeReady}
        scopeRequirements={scopeRequirements}
        postCreateAction={postCreateAction}
        setPostCreateAction={setPostCreateAction}
        saving={saving}
        editingPolicy={editingPolicy}
      />
    )}
  </CompositeEditorSwitch>
);

const Policies: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useMode();
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canReadPolicies = canAccessWarehouseSurface(capabilities, 'policies');
  const canWritePolicies = canManagePolicies(capabilities);
  const canDeployPoliciesFlag = canDeployPolicies(capabilities);
  const { siteId, orgId } = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const qSite = (sp.get('site') || sp.get('site_id') || '').trim();
      const qOrg = (sp.get('org') || sp.get('org_id') || '').trim();
      const scope = ensureUiScope();
      const site = qSite || String(scope?.siteId || '').trim();
      const org = qOrg || String(scope?.orgId || '').trim();
      return { siteId: site, orgId: org };
    } catch {
      return { siteId: '', orgId: '' };
    }
  }, [location.search]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);
  const [siteComplexity, setSiteComplexity] = useState<SiteComplexity | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [policyDetailsId, setPolicyDetailsId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PolicyStatusFilter>('all');
  const [packFilter, setPackFilter] = useState<PolicyPackFilter>('all');
  const [pageMenuAnchor, setPageMenuAnchor] = useState<null | HTMLElement>(null);
  const [policyMenuAnchor, setPolicyMenuAnchor] = useState<null | HTMLElement>(null);
  const [policyMenuRowId, setPolicyMenuRowId] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning'; autoHideDuration?: number }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Operator overrides (Phase J)
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePolicy, setOverridePolicy] = useState<Policy | null>(null);
  const [overrideDisable, setOverrideDisable] = useState(false);
  const [overrideSeverity, setOverrideSeverity] = useState<'low' | 'medium' | 'high' | 'critical' | ''>('');
  const [overrideCooldownSeconds, setOverrideCooldownSeconds] = useState<string>('');
  const [overrideDurationMinutes, setOverrideDurationMinutes] = useState<number>(120);
  const [overrideReason, setOverrideReason] = useState<string>('maintenance');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // F-10 (P1): show policy deploy lint errors (missing/disabled geometry) in a user-friendly way
  const [deployLintOpen, setDeployLintOpen] = useState(false);
  const [deployLintTitle, setDeployLintTitle] = useState<string>('Deploy blocked');
  const [deployLintErrors, setDeployLintErrors] = useState<any[]>([]);
  const [clockGateOpen, setClockGateOpen] = useState(false);
  const [clockGateDevices, setClockGateDevices] = useState<string[]>([]);
  const [clockGatePolicy, setClockGatePolicy] = useState<string>('');

  // Smooth policy creation flow: create → activate → deploy (optional)
  const [postCreateAction, setPostCreateAction] = useState<'draft' | 'activate' | 'activate_deploy'>('activate_deploy');
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<PolicyCreateData>>({
    policy_type: 'zone_intrusion',
    name: '',
    description: '',
    scope: {
      zone_ids: [],
      entity_types: ['person'],
    },
    config: {},
    alert: {
      severity: 'medium',
    },
    enabled: true,
  });

  // Zone selection state
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [selectedEdgeDeviceId, setSelectedEdgeDeviceId] = useState<string>('');
  const [availableZones, setAvailableZones] = useState<Zone[]>([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [lineDirection, setLineDirection] = useState<'both' | 'a_to_b' | 'b_to_a'>('both');
  const [editorStep, setEditorStep] = useState<number>(0);
  const [editorTab, setEditorTab] = useState<'scope' | 'hours' | 'actions'>('scope');
  const [weekGrid, setWeekGrid] = useState<Record<string, boolean[]>>({});
  const [subject, setSubject] = useState<PolicySubject>('person');
  const [selectedPolicyPack, setSelectedPolicyPack] = useState<PackKey | null>(null);
  const [selectedPolicyAlias, setSelectedPolicyAlias] = useState<string>('');
  const [clockStatusByDevice, setClockStatusByDevice] = useState<Record<string, boolean | null>>({});

  // Delete all policies on device
  const [deleteAllDeviceOpen, setDeleteAllDeviceOpen] = useState(false);
  const [deleteAllDeviceId, setDeleteAllDeviceId] = useState<string>('');
  const [deleteAllDeviceSaving, setDeleteAllDeviceSaving] = useState(false);

  // Epic 11: canary cohort deploy options (when a canary group exists for the site)
  const [canaryGroup, setCanaryGroup] = useState<{ id: string; name: string } | null>(null);
  const [scopeFull, setScopeFull] = useState<number | null>(null);
  const [scopeCanary, setScopeCanary] = useState<number | null>(null);
  const policiesPageFetchGenRef = useRef(0);
  const policiesDeployMetaGenRef = useRef(0);

  // Zone preview (lightweight visualization in Policies editor)
  const [zonePreviewOpen, setZonePreviewOpen] = useState(false);
  const [zonePreviewZone, setZonePreviewZone] = useState<Zone | null>(null);
  const [zonePreviewImg, setZonePreviewImg] = useState<string | null>(null);
  const [zonePreviewLoading, setZonePreviewLoading] = useState(false);
  const [zonePreviewError, setZonePreviewError] = useState<string | null>(null);

  const _zoneOverlay = useMemo(() => {
    const z = zonePreviewZone as any;
    if (!z) return null;
    const bbox = z?.bbox_norm;
    const points = z?.points_norm;
    if (Array.isArray(points) && points.length >= 3) {
      const pts = points
        .map((p: any) => (Array.isArray(p) && p.length >= 2 ? `${Number(p[0])},${Number(p[1])}` : null))
        .filter(Boolean)
        .join(' ');
      if (pts) return { kind: 'polygon' as const, points: pts };
    }
    if (Array.isArray(bbox) && bbox.length === 4) {
      const [x, y, w, h] = bbox.map((n: any) => Number(n));
      if ([x, y, w, h].every((n) => Number.isFinite(n))) return { kind: 'rect' as const, x, y, w, h };
    }
    return null;
  }, [zonePreviewZone]);

  const openZonePreview = useCallback(
    async (zone: Zone) => {
      try {
        setZonePreviewOpen(true);
        setZonePreviewZone(zone);
        setZonePreviewImg(null);
        setZonePreviewError(null);
        setZonePreviewLoading(true);

        const camId = (zone as any)?.camera_id || selectedCameraId;
        const edgeDeviceId =
          String((zone as any)?.device_id || selectedEdgeDeviceId || '').trim() ||
          String(cameras.find((c) => c.camera_id === camId)?.edge_device_id || '').trim();
        const _cam = camId ? cameras.find((c) => c.camera_id === camId && c.edge_device_id === edgeDeviceId) : undefined;
        if (!camId || !edgeDeviceId) {
          setZonePreviewError('Missing camera or edge device for thumbnail');
          return;
        }
        const dataUrl = await camerasApi.getCameraThumbnail(camId, edgeDeviceId);
        setZonePreviewImg(dataUrl);
      } catch (e: any) {
        setZonePreviewError(e?.message || 'Failed to load camera thumbnail');
      } finally {
        setZonePreviewLoading(false);
      }
    },
    [cameras, selectedCameraId, selectedEdgeDeviceId]
  );

  // F-30: Use standardized status metadata (deploymentChipColor removed, using metadata directly)

  const _initDefaultGrid = useCallback((): Record<string, boolean[]> => {
    const g: Record<string, boolean[]> = {};
    for (const d of DAYS) g[d.key] = Array.from({ length: 24 }, (_, i) => i >= 8 && i < 17 && ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d.key));
    return g;
  }, []);

  const _gridToWindows = useCallback((g: Record<string, boolean[]>) => {
    const windows: any = {};
    for (const d of DAYS) {
      const arr = g[d.key] || [];
      const dayWins: Array<{ start: string; end: string }> = [];
      let start: number | null = null;
      for (let h = 0; h <= 24; h++) {
        const on = h < 24 ? !!arr[h] : false;
        if (on && start === null) start = h;
        if (!on && start !== null) {
          const pad = (n: number) => String(n).padStart(2, '0');
          dayWins.push({ start: `${pad(start)}:00`, end: `${pad(h)}:00` });
          start = null;
        }
      }
      windows[d.key] = dayWins;
    }
    return windows;
  }, []);

  const _applyScheduleToGrid = useCallback((sched: any | undefined): Record<string, boolean[]> => {
    const g = _initDefaultGrid();
    if (!sched) return g;
    const windows = sched?.windows;
    if (windows && typeof windows === 'object') {
      for (const d of DAYS) {
        g[d.key] = Array.from({ length: 24 }, () => false);
        const wlist = windows[d.key];
        if (Array.isArray(wlist)) {
          for (const w of wlist) {
            const s = String(w?.start || '');
            const e = String(w?.end || '');
            const sh = parseInt(s.split(':')[0] || '0', 10);
            const eh = parseInt(e.split(':')[0] || '0', 10);
            for (let h = Math.max(0, sh); h < Math.min(24, eh); h++) g[d.key][h] = true;
          }
        }
      }
      return g;
    }
    // Backward compat: days + start/end
    const days = Array.isArray(sched?.days) ? sched.days.map((x: string) => String(x).toLowerCase()) : [];
    const st = String(sched?.start_time || '08:00');
    const en = String(sched?.end_time || '17:00');
    const sh = parseInt(st.split(':')[0] || '8', 10);
    const eh = parseInt(en.split(':')[0] || '17', 10);
    for (const d of DAYS) {
      g[d.key] = Array.from({ length: 24 }, (_, h) => days.includes(d.key) && h >= sh && h < eh);
    }
    return g;
  }, [_initDefaultGrid]);

  const cameraById = useMemo(() => {
    const map = new Map<string, Camera>();
    for (const cam of cameras) map.set(cam.camera_id, cam);
    return map;
  }, [cameras]);

  const zoneById = useMemo(() => {
    const map = new Map<string, Zone>();
    for (const z of zones) map.set(z.zone_id, z);
    return map;
  }, [zones]);

  const lineById = useMemo(() => {
    const map = new Map<string, Line>();
    for (const l of lines) {
      map.set(l.line_id, l);
      if (l.id) map.set(l.id, l);
    }
    return map;
  }, [lines]);

  const deriveDeviceIdsForPolicy = useCallback(
    (
      policy: Partial<PolicyCreateData> | Policy,
      opts?: { cameraId?: string; edgeDeviceId?: string; zoneIds?: string[]; lineId?: string },
    ): string[] => {
      const deviceIds = new Set<string>();
      const scope = (policy as any)?.scope || {};
      const cameraIds = Array.isArray(scope.camera_ids) ? scope.camera_ids : [];
      const zoneIds = Array.isArray(scope.zone_ids) ? scope.zone_ids : opts?.zoneIds || [];

      // Prefer explicit device scoping when present (prevents camera_id collisions across edges).
      const scopedDev = String(scope.edge_device_id || scope.device_id || '').trim();
      if (scopedDev) deviceIds.add(scopedDev);
      const optDev = String(opts?.edgeDeviceId || '').trim();
      if (optDev) deviceIds.add(optDev);

      const addDeviceForCameraId = (cameraId?: string) => {
        if (!cameraId) return;
        const cam = cameraById.get(cameraId);
        if (cam?.edge_device_id) deviceIds.add(cam.edge_device_id);
      };
      const addDeviceForZoneId = (zoneId?: string) => {
        if (!zoneId) return;
        const zone = zoneById.get(zoneId);
        if (zone?.device_id) {
          deviceIds.add(zone.device_id);
          return;
        }
        addDeviceForCameraId(zone?.camera_id);
      };
      const addDeviceForLineId = (lineId?: string) => {
        if (!lineId) return;
        const line = lineById.get(lineId);
        if (line?.device_id) {
          deviceIds.add(line.device_id);
          return;
        }
        addDeviceForCameraId(line?.camera_id);
      };

      for (const cid of cameraIds) addDeviceForCameraId(cid);
      for (const zid of zoneIds) addDeviceForZoneId(zid);

      const lineId =
        opts?.lineId ||
        (policy as any)?.config?.line_id ||
        (policy as any)?.config?.line?.line_id;
      addDeviceForLineId(lineId);

      addDeviceForCameraId(opts?.cameraId);
      return Array.from(deviceIds.values());
    },
    [cameraById, lineById, zoneById],
  );

  // Query-driven UX (deep-links from zones/lines/cameras)
  const query = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const qType = (query.get('type') || '').trim();
  const qCameraId = (query.get('camera_id') || '').trim();
  const qDeviceId = (query.get('device_id') || '').trim();
  const qZoneId = (query.get('zone_id') || '').trim();
  const qPolicyId = (query.get('policy_id') || '').trim();
  const qLineId = (query.get('line_id') || '').trim();
  const qCreate = (query.get('create') || '').trim() === '1';
  const qPack = normalizePackKey(query.get('pack'));
  const qReturnTo = (query.get('return_to') || '').trim().toLowerCase();
  const qReturnDevice = (query.get('return_device') || '').trim();
  const qReturnStepRaw = (query.get('return_step') || '').trim();
  const qReturnStep =
    Number.isFinite(Number(qReturnStepRaw)) && qReturnStepRaw !== ''
      ? Math.max(0, Math.floor(Number(qReturnStepRaw)))
      : 2;

  useEffect(() => {
    setPackFilter(qPack || 'all');
  }, [qPack]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPackFilter(qPack || 'all');
    if (qPack || qType || qCameraId || qDeviceId || qZoneId || qLineId || qPolicyId) {
      navigate('/policies');
    }
  };
  const handleBackFromPolicies = () => {
    if (qReturnTo === 'setup') {
      const params = new URLSearchParams();
      if (qReturnDevice) {
        params.set('device', qReturnDevice);
      }
      params.set('step', String(qReturnStep));
      navigate(`/setup?${params.toString()}`);
      return;
    }
    navigate(-1);
  };

  const fetchPolicies = useCallback(async () => {
    try {
      const moduleScope = mode === 'ocr' ? 'ocr' : 'warehouse';
      const res = await withTimeout(policiesApi.listPolicies(siteId, undefined, moduleScope, orgId), 'Policies');
      setPolicies(res.policies || []);
      try {
        const c = await withTimeout(sitesApi.getComplexity(siteId), 'Site complexity');
        setSiteComplexity(c);
      } catch {
        // optional; ignore if gated by auth or not supported
      }
    } catch (err: any) {
      console.error('Failed to fetch policies:', err);
      const msg = err?.response?.data?.error || err?.message || 'Unknown error';
      setSnackbar({ open: true, message: `Failed to load policies: ${msg}`, severity: 'error' });
    } finally {
      setPoliciesLoaded(true);
      setLoading(false);
    }
  }, [siteId, orgId, mode]);

  const fetchZones = useCallback(async () => {
    try {
      const moduleScope = mode === 'ocr' ? 'ocr' : 'warehouse';
      const res = await withTimeout(zonesApi.listZones(undefined, undefined, undefined, undefined, true, moduleScope), 'Zones');
      setZones(res.zones || []);
    } catch (err: any) {
      console.error('Failed to fetch zones:', err);
    }
  }, [mode]);

  const fetchCameras = useCallback(async () => {
    try {
      // Registry-only list: edge runtime probes make /cameras take ~9s and used to race the
      // 10s page timeout, leaving the Create Policy camera dropdown empty.
      const res = await withTimeout(
        camerasApi.listCameras(siteId, undefined, {
          includeEdgeRuntime: false,
          scheduleAutoPreflight: false,
        }),
        'Cameras',
        30000,
      );
      setCameras(res.items || []);
    } catch (err: any) {
      console.error('Failed to fetch cameras:', err);
      setCameras([]);
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to load cameras: ${err?.message || 'unknown error'}. Refresh Policies and try again.`,
      });
    }
  }, [siteId]);

  const fetchLines = useCallback(async () => {
    try {
      const res = await withTimeout(linesApi.listLines(undefined, undefined, true), 'Lines');
      setLines(res.lines || []);
    } catch (err: any) {
      console.error('Failed to fetch lines:', err);
    }
  }, []);

  const fetchClockStatus = useCallback(async (deviceIds: string[]) => {
    const unique = Array.from(new Set(deviceIds.filter(Boolean)));
    if (unique.length === 0) return;
    const updates: Record<string, boolean | null> = {};
    await Promise.all(
      unique.map(async (deviceId) => {
        try {
          const resp = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/health`);
          const unsynced = resp?.data?.clock?.clock_unsynced;
          updates[deviceId] = typeof unsynced === 'boolean' ? unsynced : null;
        } catch {
          updates[deviceId] = null;
        }
      }),
    );
    setClockStatusByDevice((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    if (!siteId) {
      setLoading(false);
      setPoliciesLoaded(true);
      setPolicies([]);
      setCameras([]);
      setSiteComplexity(null);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Org/site scope is not set. Set it via Incidents → Scope (e.g. acme/yyz) or open /policies?site=yyz',
      });
      return;
    }
    const gen = ++policiesPageFetchGenRef.current;
    setLoading(true);
    setPoliciesLoaded(false);
    Promise.all([fetchPolicies(), fetchZones(), fetchCameras()])
      .finally(() => {
        if (gen === policiesPageFetchGenRef.current) setLoading(false);
      });
    scheduleDeferredWork(() => {
      if (gen !== policiesPageFetchGenRef.current) return;
      void fetchLines();
    });
  }, [siteId, fetchPolicies, fetchZones, fetchCameras, fetchLines]);

  // Epic 11: fetch canary group and blast radius for deploy UI
  useEffect(() => {
    if (!siteId || !orgId) {
      setCanaryGroup(null);
      setScopeFull(null);
      setScopeCanary(null);
      return;
    }
    const gen = ++policiesDeployMetaGenRef.current;
    let cancelled = false;
    scheduleDeferredWork(() => {
      if (cancelled || gen !== policiesDeployMetaGenRef.current) return;
      void (async () => {
      try {
        const [groupsRes, scopeFullRes] = await Promise.all([
          withTimeout(api.get(endpoints.devices.groups, { timeout: PAGE_TIMEOUT_MS }), 'Device groups'),
          withTimeout(api.get('/api/v1/deployments/scope', {
            params: { org_id: orgId, site_id: siteId, include_device_ids: false },
            timeout: PAGE_TIMEOUT_MS,
          }), 'Deploy scope'),
        ]);
        if (cancelled || gen !== policiesDeployMetaGenRef.current) return;
        const groups: Array<{ id: string; name: string; org_id?: string; site_id?: string; tags?: Record<string, unknown> }> =
          groupsRes?.data?.groups || groupsRes?.data?.items || [];
        const full = scopeFullRes?.data?.device_count ?? null;
        if (cancelled || gen !== policiesDeployMetaGenRef.current) return;
        setScopeFull(typeof full === 'number' ? full : null);

        const canary = groups.find(
          (g) =>
            String(g.org_id || '').trim() === orgId &&
            String(g.site_id || '').trim() === siteId &&
            (String((g.tags as Record<string, unknown>)?.cohort || '').toLowerCase() === 'canary' ||
              String(g.name || '').toLowerCase().includes('canary'))
        );
        if (!canary) {
          if (gen === policiesDeployMetaGenRef.current) {
            setCanaryGroup(null);
            setScopeCanary(null);
          }
          return;
        }
        if (gen === policiesDeployMetaGenRef.current) {
          setCanaryGroup({ id: String(canary.id), name: String(canary.name || 'Canary') });
        }
        try {
          const scopeCanaryRes = await withTimeout(api.get('/api/v1/deployments/scope', {
            params: { org_id: orgId, site_id: siteId, group_id: canary.id, include_device_ids: false },
            timeout: PAGE_TIMEOUT_MS,
          }), 'Canary deploy scope');
          if (!cancelled && gen === policiesDeployMetaGenRef.current) {
            setScopeCanary(scopeCanaryRes?.data?.device_count ?? null);
          }
        } catch {
          if (!cancelled && gen === policiesDeployMetaGenRef.current) setScopeCanary(null);
        }
      } catch {
        if (!cancelled && gen === policiesDeployMetaGenRef.current) {
          setCanaryGroup(null);
          setScopeFull(null);
          setScopeCanary(null);
        }
      }
    })();
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, orgId]);

  useEffect(() => {
    const deviceIds = cameras.map((c) => c.edge_device_id).filter(Boolean);
    void fetchClockStatus(deviceIds);
  }, [cameras, fetchClockStatus]);

  // Apply query params after base data is loaded; supports:
  // - /policies?policy_id=... (open exact policy detail)
  // - /policies?camera_id=... (filter)
  // - /policies?create=1&pack=core_safety (pack-first create dialog)
  // - /policies?create=1&type=line_crossing&camera_id=...&line_id=... (prefill create dialog)
  // - /policies?create=1&type=zone_intrusion&camera_id=...&zone_id=... (prefill create dialog)
  useEffect(() => {
    // IMPORTANT:
    // Don't continuously overwrite the user's manual camera selection while the dialog is open.
    // Only apply URL filter selection when the dialog is closed.
    if (qCameraId && !dialogOpen) setSelectedCameraId(qCameraId);
    if (qDeviceId && !dialogOpen) setSelectedEdgeDeviceId(qDeviceId);
    if (qPolicyId && !dialogOpen) setPolicyDetailsId(qPolicyId);

    if (!qCreate) return;

    // Prevent re-applying the same query-prefill multiple times while the dialog is open.
    // Without this, the camera select can appear to "snap back" after the user picks a camera.
    // Keyed by the full relevant query tuple.
    // (We keep this ref local to the page; it resets on refresh.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const _queryKey = `${qCreate}|${qType}|${qCameraId}|${qZoneId}|${qLineId}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // (Note: useRef declared above hooks section.)

    const packDefaultAlias = qPack
      ? PACK_BY_KEY[qPack]?.policies.find((policy) => !policy.disabled && policy.policyType)?.alias || ''
      : '';
    const inferredSelection = resolvePackAndAlias({
      policyType: ((qType as any) || 'zone_intrusion'),
      subject: 'person',
      zoneIds: qZoneId ? [qZoneId] : [],
      zonesById,
    });
    const nextSelection = qPack && packDefaultAlias
      ? { packKey: qPack, optionId: packDefaultAlias }
      : { packKey: inferredSelection.packKey, optionId: inferredSelection.alias };
    const selectedAliasDefinition = getPolicyAliasDefinition(nextSelection.optionId);
    const nextType = (qType as any) || selectedAliasDefinition?.policyType || 'zone_intrusion';
    const nextSubject = (selectedAliasDefinition?.entityTypes?.[0] as PolicySubject | undefined) || 'person';
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // (We need the ref; declared above.)
    setEditingPolicy(null);
    setSelectedPolicyPack(nextSelection.packKey);
    setSelectedPolicyAlias(nextSelection.optionId);
    setEditorStep((qPack || qType || qZoneId || qLineId) ? 1 : 0);
    setEditorTab('scope');
    setSubject(nextSubject);
    setFormData((prev) => ({
      ...prev,
      policy_type: nextType,
      name: prev.name || '',
      description: prev.description || '',
      enabled: true,
      scope: {
        ...(prev.scope || {}),
        zone_ids: [],
        entity_types: [nextSubject],
        camera_ids: qCameraId ? [qCameraId] : [],
        edge_device_id: qDeviceId || (prev.scope as any)?.edge_device_id,
      },
      config: {},
      alert: prev.alert || { severity: 'medium' },
    }));

    if (qCameraId) setSelectedCameraId(qCameraId);
    if (qDeviceId) setSelectedEdgeDeviceId(qDeviceId);

    // If zone deep-link, preselect zone
    if (qZoneId) {
      setSelectedZoneIds([qZoneId]);
    } else {
      setSelectedZoneIds([]);
    }

    // If line deep-link, preselect line + direction
    if (nextType === 'line_crossing') {
      setSelectedLineId(qLineId || '');
      setLineDirection('both');
    } else {
      setSelectedLineId('');
    }

    setDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, qCameraId, qCreate, qDeviceId, qLineId, qPack, qPolicyId, qType, qZoneId]);

  // Update available zones when camera selection changes
  useEffect(() => {
    if (selectedCameraId) {
      const cameraZones = zones.filter(
        (z) =>
          z.camera_id === selectedCameraId &&
          z.enabled !== false &&
          (!selectedEdgeDeviceId || String(z.device_id || '').trim() === String(selectedEdgeDeviceId).trim()),
      );
      setAvailableZones(cameraZones);
      // Keep only zones that are still available
      setSelectedZoneIds((prev) => prev.filter((id) => cameraZones.some((z) => z.zone_id === id)));
    } else {
      // v1: camera is required; until selected, show none (prevents cross-camera geometry mixing).
      setAvailableZones([]);
      setSelectedZoneIds([]);
    }
  }, [selectedCameraId, selectedEdgeDeviceId, zones]);

  const availableLines = useMemo(() => {
    const enabled = lines.filter((l) => l.enabled !== false);
    if (!selectedCameraId) return enabled;
    return enabled.filter(
      (l) =>
        l.camera_id === selectedCameraId &&
        (!selectedEdgeDeviceId || String(l.device_id || '').trim() === String(selectedEdgeDeviceId).trim()),
    );
  }, [lines, selectedCameraId, selectedEdgeDeviceId]);

  const zonesById = useMemo(
    () => new Map(zones.map((zone) => [String(zone.zone_id), zone])),
    [zones],
  );
  const selectedCameraProfileId = useMemo(() => {
    const selected = cameras.find(
      (camera) =>
        String(camera.camera_id || '') === String(selectedCameraId || '') &&
        (!selectedEdgeDeviceId || String(camera.edge_device_id || '') === String(selectedEdgeDeviceId || '')),
    );
    return (selected?.profile_id || '') as '' | 'A' | 'B' | 'C' | 'D';
  }, [cameras, selectedCameraId, selectedEdgeDeviceId]);

  const selectedPackGroup = selectedPolicyPack ? PACK_BY_KEY[selectedPolicyPack] : null;
  const selectedPolicyOption = selectedPolicyAlias ? getPolicyAliasDefinition(selectedPolicyAlias) : undefined;
  const selectedPolicySubject = subject;
  const selectionReady = Boolean(selectedPolicyOption?.policyType) || formData.policy_type === COMPOSITE_POLICY_TYPE;
  const isWorkerDownPolicy = formData.policy_type === 'worker_down';
  const isWave2InteractionRiskPolicy =
    formData.policy_type === 'forklift_proximity' ||
    formData.policy_type === 'pedestrian_near_moving_forklift' ||
    formData.policy_type === 'forklift_speeding';
  const allowsWholeViewScope =
    formData.policy_type === 'ppe_compliance' ||
    formData.policy_type === 'occupancy' ||
    formData.policy_type === 'worker_down' ||
    formData.policy_type === 'forklift_proximity' ||
    formData.policy_type === 'pedestrian_near_moving_forklift' ||
    formData.policy_type === 'forklift_speeding';
  const scopeRequirements: string[] = [];
  if (formData.policy_type !== COMPOSITE_POLICY_TYPE && !selectedCameraId) scopeRequirements.push('Select a camera');
  if (formData.policy_type !== COMPOSITE_POLICY_TYPE && isWorkerDownPolicy && selectedCameraId && selectedCameraProfileId && selectedCameraProfileId !== 'B') {
    scopeRequirements.push('Select a Worker Protection Camera (Profile B)');
  }
  if (formData.policy_type !== COMPOSITE_POLICY_TYPE && isWave2InteractionRiskPolicy && selectedCameraId && selectedCameraProfileId && selectedCameraProfileId !== 'C') {
    scopeRequirements.push('Select a Forklift ROI Safety Camera (Profile C)');
  }
  if (formData.policy_type !== COMPOSITE_POLICY_TYPE && formData.policy_type === 'line_crossing') {
    if (!selectedLineId) scopeRequirements.push('Select a line');
  } else if (formData.policy_type !== COMPOSITE_POLICY_TYPE && !allowsWholeViewScope && selectedZoneIds.length === 0) {
    scopeRequirements.push('Select at least one zone');
  }
  const scopeReady = scopeRequirements.length === 0;
  const effectivePostCreateAction =
    formData.policy_type === COMPOSITE_POLICY_TYPE || (!editingPolicy && isWorkerDownPolicy) ? 'draft' : postCreateAction;
  const canSavePolicy = Boolean(canWritePolicies && !saving && selectionReady && String(formData.name || '').trim() && scopeReady);
  const primarySaveLabel = editingPolicy
    ? 'Update Policy'
    : effectivePostCreateAction === 'activate_deploy'
      ? 'Create and Deploy'
      : effectivePostCreateAction === 'activate'
        ? 'Create and Activate'
        : 'Create Draft';

  useEffect(() => {
    if ((formData.policy_type === COMPOSITE_POLICY_TYPE || (!editingPolicy && isWorkerDownPolicy)) && postCreateAction !== 'draft') {
      setPostCreateAction('draft');
    }
  }, [editingPolicy, formData.policy_type, isWorkerDownPolicy, postCreateAction]);

  const applyPolicyAliasSelection = useCallback((optionId: string) => {
    const nextOption = getPolicyAliasDefinition(optionId);
    if (!nextOption?.policyType || nextOption.disabled) return;
    const next = nextOption.policyType;
    setSelectedPolicyAlias(optionId);
    setSelectedPolicyPack(getPackKeyForAlias(optionId));
    setFormData((prev) => {
      const nextCfg = {
        ...((nextOption.defaults?.config as any) || {}),
        ...(prev.config || {}),
      } as any;
      if (next === 'ppe_compliance') {
        const current = nextCfg?.required_items;
        const arr = Array.isArray(current) ? current.map((x: any) => String(x)) : [];
        if (arr.length === 0) nextCfg.required_items = DEFAULT_PPE_REQUIRED_ITEMS;
      }
      return {
        ...prev,
        policy_type: next,
        name: prev.name,
        scope: {
          ...(prev.scope || {}),
          geometry_mode: nextOption.defaults?.geometryMode || (prev.scope as any)?.geometry_mode,
        },
        config: nextCfg,
      };
    });
    if (nextOption.entityTypes && nextOption.entityTypes.length > 0) {
      setSubject(getPolicySubjectFromEntityTypes(nextOption.entityTypes));
    }
    if (next === 'line_crossing') {
      setSelectedZoneIds([]);
    } else {
      setSelectedLineId('');
    }
  }, []);

  const filteredPolicies = useMemo(() => {
    let out = [...(policies || [])];
    if (qPack) {
      out = out.filter((p) => {
        const resolved = resolvePackAndAlias({
          policyType: p.policy_type,
          subject: getPolicySubjectFromEntityTypes(p.scope?.entity_types),
          zoneIds: p.scope?.zone_ids,
          zonesById,
          uiContext: readPolicyUiContext(p),
        });
        return resolved.packKey === qPack;
      });
    }
    if (qType) out = out.filter((p) => p.policy_type === qType);
    if (qCameraId) {
      out = out.filter((p) => {
        if ((p.scope?.camera_ids || []).includes(qCameraId)) return true;
        const target = p.target && typeof p.target === 'object' ? p.target : {};
        return String(target.camera_id || '').trim() === qCameraId;
      });
    }
    if (qDeviceId) {
      out = out.filter((p) => {
        const target = p.target && typeof p.target === 'object' ? p.target : {};
        const edgeDeviceId = String(
          target.edge_device_id || (p as any)?.edge_device_id || '',
        ).trim();
        return edgeDeviceId === qDeviceId;
      });
    }
    if (qZoneId) {
      out = out.filter((p) => (p.scope?.zone_ids || []).includes(qZoneId));
    }
    if (qLineId) {
      out = out.filter((p) => {
        const lid = (p as any)?.config?.line_id || (p as any)?.config?.line?.line_id;
        return p.policy_type === 'line_crossing' && lid === qLineId;
      });
    }
    return out;
  }, [policies, qPack, qType, qCameraId, qDeviceId, qZoneId, qLineId, zonesById]);

  const sortPolicyViews = (items: ReturnType<typeof buildPolicyTroubleshootingView>[]) => {
    const bucketRank: Record<string, number> = {
      attention: 0,
      deploying: 1,
      pending_deploy: 2,
      healthy: 3,
      draft: 4,
    };
    return items
      .sort((a, b) => {
        const bucketDelta = (bucketRank[a.bucket] ?? 99) - (bucketRank[b.bucket] ?? 99);
        if (bucketDelta !== 0) return bucketDelta;
        return String(a.policy.name || '').localeCompare(String(b.policy.name || ''));
      });
  };

  const allPolicyViews = useMemo(
    () => sortPolicyViews((policies || []).map((policy) => buildPolicyTroubleshootingView({ policy, cameras, zones, lines }))),
    [policies, cameras, zones, lines],
  );

  const filteredPolicyViews = useMemo(
    () => sortPolicyViews(filteredPolicies.map((policy) => buildPolicyTroubleshootingView({ policy, cameras, zones, lines }))),
    [filteredPolicies, cameras, zones, lines],
  );

  const groupedPolicyViews = useMemo(() => {
    return {
      attention: filteredPolicyViews.filter((view) => view.bucket === 'attention'),
      deploying: filteredPolicyViews.filter((view) => view.bucket === 'deploying'),
      pending_deploy: filteredPolicyViews.filter((view) => view.bucket === 'pending_deploy'),
      healthy: filteredPolicyViews.filter((view) => view.bucket === 'healthy'),
      draft: filteredPolicyViews.filter((view) => view.bucket === 'draft'),
    };
  }, [filteredPolicyViews]);

  const allPresentedPolicies = useMemo(() => {
    const lookups = { cameras, zones, lines, zonesById };
    return (policies || []).map((p) => presentPolicy(p, lookups));
  }, [policies, cameras, zones, lines, zonesById]);

  const presentedPolicies = useMemo(() => {
    const lookups = { cameras, zones, lines, zonesById };
    return filteredPolicies.map((p) => presentPolicy(p, lookups));
  }, [filteredPolicies, cameras, zones, lines, zonesById]);

  const buildPolicyRows = (
    views: ReturnType<typeof buildPolicyTroubleshootingView>[],
    presented: PresentedPolicy[],
  ): PolicyManagementRow[] =>
    views.map((view) => {
      const match =
        presented.find(
          (item) => item.source.id === view.policy.id || item.source.policy_id === view.policy.policy_id,
        ) || null;
      const displayName = derivePolicyDisplayName(view.policy, match);
      const statusLabel = derivePolicyStatusLabel(view.policy, view);
      const deploymentLabel = derivePolicyDeploymentLabel(view.policy, view);
      const scopeIndicator = deriveScopeIndicator(view.policy, view);
      const isActive = statusLabel === CONFIG_STATE_LABELS.live;
      const isDraft = statusLabel === CONFIG_STATE_LABELS.draft;
      const isInactive = isDraft;
      const isInvalid = statusLabel === CONFIG_STATE_LABELS.blocked;
      const needsZone = scopeIndicator === CONFIG_SCOPE_LABELS.needsZoneScope;
      const pendingDeploy = view.state === 'pending_deploy';
      const targetDeviceLabel = view.targetDeviceId || CONFIG_SCOPE_LABELS.noEdgeDevice;
      const targetCameraLabel = match?.cameraLabel || view.targetCameraId || CONFIG_SCOPE_LABELS.noCameraStream;

      return {
        id: view.policy.id,
        policy: view.policy,
        view,
        presented: match,
        displayName,
        secondaryId:
          view.policy.policy_id && view.policy.policy_id !== displayName ? view.policy.policy_id : '',
        packKey: match?.packKey || 'core_safety',
        packLabel: match?.packLabel || 'Unassigned',
        targetLabel: view.targetLabel || [targetDeviceLabel, targetCameraLabel].filter(Boolean).join(' / '),
        targetDeviceLabel,
        targetCameraLabel,
        scopeLabel: match?.scopeSummary || view.lineSummary || view.zoneSummary || `No ${CONFIG_SCOPE_LABELS.area.toLowerCase()} scope`,
        statusLabel,
        deploymentLabel,
        lastUpdatedLabel: formatDateTime((view.policy as any).updated_at || (view.policy as any).created_at),
        scopeIndicator: scopeIndicator || undefined,
        isActive,
        isDraft,
        isInactive,
        isInvalid,
        needsZone,
        pendingDeploy,
      };
    });

  const allPolicyRows = useMemo(
    () => buildPolicyRows(allPolicyViews, allPresentedPolicies),
    [allPolicyViews, allPresentedPolicies],
  );

  const packSummaries = useMemo<PolicyPackSummary[]>(
    () =>
      POLICY_PACKS.map((pack) => {
        const rows = allPolicyRows.filter((row) => row.packKey === pack.key);
        return {
          key: pack.key,
          label: pack.label,
          valueProp: pack.valueProp,
          count: rows.length,
          activeCount: rows.filter((row) => row.isActive).length,
          inactiveCount: rows.filter((row) => row.isInactive || row.isDraft || row.isInvalid).length,
        };
      }),
    [allPolicyRows],
  );

  const policySummary = useMemo(
    () => ({
      total: allPolicyRows.length,
      active: allPolicyRows.filter((row) => row.isActive).length,
      inactive: allPolicyRows.filter((row) => row.isInactive || row.isDraft || row.isInvalid).length,
      needsZone: allPolicyRows.filter((row) => row.needsZone).length,
      pendingDeploy: allPolicyRows.filter((row) => row.pendingDeploy).length,
      packCount: PACK_DISPLAY_ORDER.length,
    }),
    [allPolicyRows],
  );

  const policyRows = useMemo(
    () => buildPolicyRows(filteredPolicyViews, presentedPolicies),
    [filteredPolicyViews, presentedPolicies],
  );

  const filteredPolicyRows = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    return policyRows.filter((row) => {
      if (packFilter !== 'all' && row.packKey !== packFilter) return false;
      if (search) {
        const haystack = [
          row.displayName,
          row.secondaryId,
          row.packLabel,
          row.targetLabel,
          row.targetDeviceLabel,
          row.targetCameraLabel,
          row.scopeLabel,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (statusFilter === 'active' && !row.isActive) return false;
      if (statusFilter === 'inactive' && !row.isInactive) return false;
      if (statusFilter === 'draft' && !row.isDraft) return false;
      if (statusFilter === 'invalid' && !row.isInvalid) return false;
      if (statusFilter === 'needs_zone' && !row.needsZone) return false;
      if (statusFilter === 'pending_deploy' && !row.pendingDeploy) return false;
      return true;
    });
  }, [policyRows, packFilter, searchQuery, statusFilter]);

  const selectedPolicyView = useMemo(
    () => allPolicyViews.find((view) => view.policy.id === policyDetailsId || view.policy.policy_id === policyDetailsId) || null,
    [allPolicyViews, policyDetailsId],
  );

  const selectedPolicyPresented = useMemo(
    () => allPresentedPolicies.find((p) => p.source.id === policyDetailsId || p.source.policy_id === policyDetailsId) || null,
    [allPresentedPolicies, policyDetailsId],
  );

  const handleDeploy = useCallback(
    async (policy: Policy, groupId?: string) => { if (blockCompositeRuntime(policy.policy_type, setSnackbar)) return;
      try {
        if (isTimeBasedSchedule(policy.schedule)) {
          const deviceIds = deriveDeviceIdsForPolicy(policy);
          const unsynced = deviceIds.filter((id) => clockStatusByDevice[id] === true);
          if (unsynced.length > 0) {
            setClockGatePolicy(policy.name || policy.policy_id);
            setClockGateDevices(unsynced);
            setClockGateOpen(true);
            return;
          }
        }

        // A-53: lint before deploy (beyond schema). If lint endpoint fails (transient), do not block deploy.
        try {
          const lint = await policiesApi.lintPolicy(siteId, policy.policy_id);
          const lintErrors = lint?.lint_errors;
          if (Array.isArray(lintErrors) && lintErrors.length > 0) {
            setDeployLintTitle(`Lint failed: ${policy.name}`);
            setDeployLintErrors(lintErrors);
            setDeployLintOpen(true);
            setSnackbar({ open: true, message: 'Deploy blocked by lint errors.', severity: 'error' });
            return;
          }
        } catch (lintErr: any) {
          const resp = lintErr?.response;
          const data = resp?.data;
          const lintErrors = data?.lint_errors;
          if (resp?.status === 400 && Array.isArray(lintErrors) && lintErrors.length > 0) {
            setDeployLintTitle(`Lint failed: ${policy.name}`);
            setDeployLintErrors(lintErrors);
            setDeployLintOpen(true);
            setSnackbar({ open: true, message: 'Deploy blocked by lint errors.', severity: 'error' });
            return;
          }
        }

        const res = await policiesApi.deployPolicy(siteId, policy.policy_id, groupId ? { groupId } : undefined);
        const warningCount =
          (Array.isArray(res?.preflight_warnings) ? res.preflight_warnings.length : 0) +
          (Array.isArray(res?.arming_warnings) ? res.arming_warnings.length : 0);
        setSnackbar({
          open: true,
          message: res?.deployment_id
            ? `Deployment ${res.deployment_id} started for ${policy.name}${warningCount ? ` (${warningCount} warning${warningCount === 1 ? '' : 's'})` : ''}`
            : `Deployment started for ${policy.name}${warningCount ? ` (${warningCount} warning${warningCount === 1 ? '' : 's'})` : ''}`,
          severity: 'success',
        });
        // Refresh state machine (DEPLOYING -> DEPLOYED/FAILED/TIMEOUT)
        fetchPolicies();
      } catch (err: any) {
        console.error('Failed to deploy policy:', err?.response?.data || err);
        const resp = err?.response;
        const data = resp?.data;
        const lintErrors = data?.lint_errors;
        if (resp?.status === 400 && data?.error === 'policy_geometry_lint_failed' && Array.isArray(lintErrors)) {
          setDeployLintTitle(`Deploy blocked: ${policy.name}`);
          setDeployLintErrors(lintErrors);
          setDeployLintOpen(true);
          // Bundle-version regression fix: also surface a snackbar so the
          // operator gets feedback even if the dialog is dismissed or behind
          // another UI element. Previously the dialog was the only signal
          // and the deploy click looked like a no-op.
          setSnackbar({
            open: true,
            message: `Deploy blocked by lint errors. ${formatDeployReasonCode('policy_geometry_lint_failed') || ''}`.trim(),
            severity: 'error',
          });
          return;
        }
        const errorCode = typeof data?.error === 'string' ? data.error : '';
        if (resp?.status === 409 && errorCode === 'zone_reference_missing') {
          const missingZones = (data?.missing_zone_ids || []).filter(Boolean);
          const affected = (data?.policies_affected || []).filter(Boolean);
          const lint = Array.isArray(data?.lint_errors) ? data.lint_errors : [];
          if (lint.length) {
            setDeployLintTitle(`Deploy blocked: missing zone references`);
            setDeployLintErrors(lint);
            setDeployLintOpen(true);
          }
          const zoneHint = missingZones.length ? ` Missing zone ID(s): ${missingZones.join(', ')}.` : '';
          const affectedHint = affected.length ? ` Affected policies: ${affected.join(', ')}.` : '';
          setSnackbar({
            open: true,
            autoHideDuration: 15_000,
            message: `Deploy blocked: zone references are broken.${zoneHint}${affectedHint} Check the zone still exists and matches the policy's camera/device target.`,
            severity: 'error',
          });
          return;
        }
        // Bundle-version regression fix: prefer the friendly reason_code
        // string (e.g. zone_plane_not_converged, BUNDLE_VERSION_REGRESSION)
        // when present, falling back to the raw API error message. The
        // formatPolicyApiError helper now also defends against `detail: []`.
        const apiMsg = formatPolicyApiError(err, 'Failed to deploy policy');
        const reasonCode = data?.reason_code ?? data?.error?.code;
        const friendlyReason = formatDeployReasonCode(reasonCode);
        const composed = friendlyReason
          ? `${friendlyReason} (${apiMsg})`
          : apiMsg;
        setSnackbar({ open: true, message: `Deploy failed: ${composed}`, severity: 'error' });
      }
    },
    [clockStatusByDevice, deriveDeviceIdsForPolicy, fetchPolicies, siteId],
  );

  const handleActivate = useCallback(
    async (policy: Policy) => { if (blockCompositeRuntime(policy.policy_type, setSnackbar)) return;
      try {
        await policiesApi.activatePolicy(policy.policy_id, { org_id: orgId, site_id: siteId });
        setSnackbar({ open: true, message: `Activated: ${policy.name}`, severity: 'success' });
        fetchPolicies();
      } catch (err: any) {
        const rawError = err?.response?.data?.error;
        const apiErrorCode = typeof rawError === 'string' ? rawError : String(rawError?.code || '').trim();
        if (err?.response?.status === 409 && apiErrorCode === 'policy_lint_blocked') {
          const issues = Array.isArray(rawError?.issues) ? rawError.issues : [];
          if (issues.length) {
            setDeployLintTitle(`Activate blocked: ${policy.name}`);
            setDeployLintErrors(issues);
            setDeployLintOpen(true);
          }
        }
        const apiMsg = formatPolicyApiError(err, 'Failed to activate policy');
        setSnackbar({ open: true, message: `Activate failed: ${apiMsg}`, severity: 'error' });
      }
    },
    [fetchPolicies, orgId, siteId],
  );

  const handleDeactivate = useCallback(
    async (policy: Policy) => {
      if (!window.confirm(`Deactivate policy "${policy.name}"?`)) return;
      try {
        await policiesApi.deactivatePolicy(policy.policy_id, { org_id: orgId, site_id: siteId });
        setSnackbar({ open: true, message: `Deactivated: ${policy.name}`, severity: 'success' });
        fetchPolicies();
      } catch (err: any) {
        const apiMsg = formatPolicyApiError(err, 'Failed to deactivate policy');
        setSnackbar({ open: true, message: `Deactivate failed: ${apiMsg}`, severity: 'error' });
      }
    },
    [fetchPolicies, orgId, siteId],
  );

  const lintFixHref = useCallback((e: any): string | null => {
    const kind = String(e?.kind || '');
    const zoneId = e?.zone_id || e?.zoneId;
    const lineId = e?.line_id || e?.lineId;
    const cameraId = e?.camera_id || e?.cameraId;
    if (kind.includes('zone') && zoneId) return `/zones?zone_id=${encodeURIComponent(String(zoneId))}`;
    if (kind.includes('line') && lineId) return `/cameras?line_id=${encodeURIComponent(String(lineId))}`;
    if (kind.includes('camera') && cameraId) return `/cameras/${encodeURIComponent(String(cameraId))}`;
    return null;
  }, []);

  const handleOpenDialog = (policy?: Policy) => { if (openCompositePolicy(policy, { setEditingPolicy, setSelectedPolicyPack, setSelectedPolicyAlias, setEditorStep, setFormData, setPostCreateAction, setDialogOpen })) return;
    if (policy) {
      const policySubject =
        policy.policy_type === 'forklift_proximity' || policy.policy_type === 'pedestrian_near_moving_forklift'
          ? 'person'
          : policy.policy_type === 'forklift_speeding'
            ? 'forklift'
          : getPolicySubjectFromEntityTypes((policy.scope as any)?.entity_types);
      const selection = resolvePolicyEditorSelection({
        policyType: policy.policy_type,
        subject: policySubject,
        zoneIds: policy.scope?.zone_ids,
        zonesById,
        uiContext: readPolicyUiContext(policy),
      });
      setEditingPolicy(policy);
      setSelectedPolicyPack(selection.packKey);
      setSelectedPolicyAlias(selection.optionId);
      setEditorStep(2);
      setEditorTab('scope');
      setFormData({
        policy_type: policy.policy_type,
        name: policy.name,
        description: policy.description || '',
        scope: policy.scope,
        config: policy.config,
        // Some older rows store timing as NULL; API validation expects an object if present.
        timing: (policy.timing && typeof policy.timing === 'object') ? policy.timing : undefined,
        schedule: policy.schedule,
        alert: policy.alert || { severity: 'medium' },
        escalation: policy.escalation,
        pattern: policy.pattern,
        actions: (policy as any).actions ?? undefined,
        enabled: policy.enabled !== false,
      });
      // Ensure PPE has explicit required_items in state; otherwise we can show defaults
      // in the UI while save-time validation sees an empty config.
      if (policy.policy_type === 'ppe_compliance') {
        const cfg = policy.config && typeof policy.config === 'object' ? { ...(policy.config as any) } : {};
        const current = cfg?.required_items;
        const arr = Array.isArray(current) ? current.map((x: any) => String(x)) : [];
        if (arr.length === 0) cfg.required_items = DEFAULT_PPE_REQUIRED_ITEMS;
        setFormData((prev) => ({ ...prev, config: cfg }));
      }
      setSelectedZoneIds(policy.scope?.zone_ids || []);
      setSelectedLineId((policy.config as any)?.line_id || (policy.config as any)?.line?.line_id || '');
      setLineDirection(((policy.config as any)?.direction as any) || 'both');
      setSubject(policySubject);
      // Try to infer camera + edge device from scope/zones (prevents cross-device zone duplication).
      const scopeEdgeDeviceId = String((policy.scope as any)?.edge_device_id || '').trim();
      const policyZones = zones.filter((z) => policy.scope?.zone_ids?.includes(z.zone_id));

      // For camera-scoped policies (like line_crossing), prefer explicit camera_ids.
      // Else infer from the first zone.
      const scopedCameraId =
        (policy.scope?.camera_ids && policy.scope.camera_ids.length > 0 ? String(policy.scope.camera_ids[0] || '').trim() : '') ||
        String(policyZones[0]?.camera_id || '').trim();
      if (scopedCameraId) setSelectedCameraId(scopedCameraId);

      // Edge device id is required to disambiguate zone_ids reused across devices.
      // Prefer explicit scope.edge_device_id, else infer from selected camera registry, else from zone.device_id.
      let inferredEdgeDeviceId = scopeEdgeDeviceId;
      if (!inferredEdgeDeviceId && scopedCameraId) {
        const cam = cameras.find((c) => String(c.camera_id || '').trim() === scopedCameraId);
        const dev = String((cam as any)?.edge_device_id || '').trim();
        if (dev) inferredEdgeDeviceId = dev;
      }
      if (!inferredEdgeDeviceId) {
        const dev = String((policyZones[0] as any)?.device_id || '').trim();
        if (dev) inferredEdgeDeviceId = dev;
      }
      setSelectedEdgeDeviceId(inferredEdgeDeviceId || '');
      // Editing: don't auto-deploy on save
      setPostCreateAction('draft');
    } else {
      setEditingPolicy(null);
      setSelectedPolicyPack(null);
      setSelectedPolicyAlias('');
      setEditorStep(0);
      setEditorTab('scope');
      setFormData({
        policy_type: 'zone_intrusion',
        name: '',
        description: '',
        scope: {
          zone_ids: [],
          entity_types: ['person'],
        },
        config: {},
        schedule: undefined,
        alert: {
          severity: 'medium',
        },
        escalation: undefined,
        pattern: undefined,
        actions: undefined,
        enabled: true,
      });
      setSelectedZoneIds([]);
      setSelectedCameraId('');
      setSelectedEdgeDeviceId('');
      setSelectedLineId('');
      setLineDirection('both');
      setSubject('person');
      // New policy: default to one-click create → activate → deploy
      setPostCreateAction('activate_deploy');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPolicy(null);
    setEditorStep(0);
    setEditorTab('scope');
  };

  // Keep weekGrid in sync with current schedule when opening/editing.
  useEffect(() => {
    if (!dialogOpen) return;
    const sched = (formData as any)?.schedule;
    setWeekGrid(_applyScheduleToGrid(sched));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formData.schedule sufficient
  }, [dialogOpen, formData.schedule, _applyScheduleToGrid]);

  const handleSave = async () => {
    let createdPolicy: any = null; let createdPolicyActivated = false;
    try {
      setSaving(true); if (await saveCompositePolicy({ formData, editingPolicy, siteId, orgId, sourcePolicies: policies, createPolicy: policiesApi.createPolicy, updatePolicy: policiesApi.updatePolicy, setSnackbar, onSaved: () => { handleCloseDialog(); fetchPolicies(); } })) return;
      const isLineCrossing = formData.policy_type === 'line_crossing';
      const isPpe = formData.policy_type === 'ppe_compliance';
      const isOcc = formData.policy_type === 'occupancy';
      const isWorkerDown = formData.policy_type === 'worker_down';
      const isForkliftProximity = formData.policy_type === 'forklift_proximity';
      const isPedestrianNearMovingForklift =
        formData.policy_type === 'pedestrian_near_moving_forklift';
      const isForkliftSpeeding = formData.policy_type === 'forklift_speeding';
      const isForkliftInteraction =
        isForkliftProximity || isPedestrianNearMovingForklift;
      const isBehaviorPolicy = isBehaviorPolicyType(String(formData.policy_type || ''));
      const isZoneIntrusion = formData.policy_type === 'zone_intrusion';
      const isRegionTransition =
        formData.policy_type === 'region_entrance' || formData.policy_type === 'region_exiting';
      const wantsWholeView = (isPpe || isOcc || isWorkerDown) && selectedZoneIds.length === 0;

      if (!selectedCameraId) {
        setSnackbar({ open: true, message: 'Camera is required (v1 rule: 1 policy = 1 camera).', severity: 'error' });
        return;
      }

      const ppeItems =
        Array.isArray((formData.config as any)?.required_items)
          ? ((formData.config as any)?.required_items as any[]).map((x) => String(x))
          : DEFAULT_PPE_REQUIRED_ITEMS;
      if (isPpe && ppeItems.length === 0) {
        setSnackbar({ open: true, message: 'Select at least 1 required PPE item.', severity: 'error' });
        return;
      }

      const normalizedConfig = isBehaviorPolicy
        ? applyBehaviorRuleUiState(
            String(formData.policy_type || ''),
            (formData.config as any) || {},
            readBehaviorRuleUiState(String(formData.policy_type || ''), (formData.config as any) || {}),
          )
        : isOcc
          ? applyOccupancyRuleUiState(
              (formData.config as any) || {},
              readOccupancyRuleUiState((formData.config as any) || {}),
            )
          : isWorkerDown
            ? applyWorkerDownRuleUiState(
                (formData.config as any) || {},
                readWorkerDownRuleUiState((formData.config as any) || {}),
              )
          : isForkliftProximity
            ? applyForkliftProximityRuleUiState(
                (formData.config as any) || {},
                readForkliftProximityRuleUiState((formData.config as any) || {}),
              )
          : isPedestrianNearMovingForklift
            ? applyPedestrianNearMovingForkliftRuleUiState(
                (formData.config as any) || {},
                readPedestrianNearMovingForkliftRuleUiState((formData.config as any) || {}),
              )
          : isForkliftSpeeding
            ? applyForkliftSpeedingRuleUiState(
                (formData.config as any) || {},
                readForkliftSpeedingRuleUiState((formData.config as any) || {}),
              )
          : isZoneIntrusion
            ? applyZoneIntrusionRuleUiState(
                (formData.config as any) || {},
                readZoneIntrusionRuleUiState((formData.config as any) || {}),
              )
            : isLineCrossing
              ? applyLineCrossingRuleUiState(
                  (formData.config as any) || {},
                  readLineCrossingRuleUiState((formData.config as any) || {}),
                )
              : isRegionTransition
                ? applyRegionTransitionRuleUiState(
                    (formData.config as any) || {},
                    readRegionTransitionRuleUiState(String(formData.policy_type || ''), (formData.config as any) || {}),
                  )
                : (formData.config || {});

      const payload: PolicyCreateData & Record<string, any> = {
        ...formData,
        // Canonical target shape (Phase 1): required for deterministic activate/deploy routing.
        ...(selectedEdgeDeviceId || selectedCameraId
          ? {
              target: {
                edge_device_id: selectedEdgeDeviceId || undefined,
                camera_id: selectedCameraId || undefined,
              },
              // Back-compat with older backend readers.
              edge_device_id: selectedEdgeDeviceId || undefined,
              camera_id: selectedCameraId || undefined,
            }
          : {}),
        scope: isLineCrossing
          ? {
              ...(formData.scope || {}),
              zone_ids: [],
              zone_tags: [],
              camera_ids: [selectedCameraId],
              // Disambiguate camera_id across multiple edges
              edge_device_id: selectedEdgeDeviceId || (formData.scope as any)?.edge_device_id,
              entity_types: isForkliftInteraction ? ['person'] : isForkliftSpeeding ? ['forklift'] : subject === 'all' ? [] : [subject],
            }
          : {
              ...formData.scope,
              zone_ids: selectedZoneIds,
              camera_ids: [selectedCameraId],
              // Disambiguate camera_id across multiple edges
              edge_device_id: selectedEdgeDeviceId || (formData.scope as any)?.edge_device_id,
              entity_types: isPpe || isForkliftInteraction ? ['person'] : isForkliftSpeeding ? ['forklift'] : subject === 'all' ? [] : [subject],
              geometry_mode: wantsWholeView ? 'whole_view' : 'zones',
            },
        config: isLineCrossing
          ? {
              ...normalizedConfig,
              line_id: selectedLineId,
              direction: lineDirection,
            }
          : isPpe
            ? { ...(formData.config || {}), required_items: ppeItems }
            : normalizedConfig,
      } as PolicyCreateData;

      const normalizedSelection = resolvePackAndAlias({
        policyType: payload.policy_type,
        subject,
        zoneIds: payload.scope?.zone_ids,
        zonesById,
        uiContext: editingPolicy ? readPolicyUiContext(editingPolicy) : null,
      });
      const uiPackKey = selectedPolicyPack || normalizedSelection.packKey;
      const uiPolicyAlias = selectedPolicyAlias || normalizedSelection.alias;
      payload.config = withPolicyReportContext(payload.config || {}, {
        pack_key: uiPackKey,
        policy_alias: uiPolicyAlias,
        version: 1,
      });

      // PUT updates must send an explicit empty array when the operator clears the
      // last outbound action; otherwise the backend preserves the previously stored
      // email/SMS/webhook action because the field is omitted from the JSON body.
      (payload as any).actions = normalizePolicyActionsForSave((payload as any).actions); normalizePatternForSave(payload, Boolean(editingPolicy)); normalizeImmediateForSave(payload);

      // API validation: if timing is present, it must be an object. Avoid sending null/strings.
      if (!(payload as any).timing || typeof (payload as any).timing !== 'object') {
        delete (payload as any).timing;
      }

      const acts = (payload as any).actions;
      if (Array.isArray(acts)) {
        for (const a of acts) {
          if (a == null || a.enabled === false) continue;
          const typ = String(a.type || '').trim();
          if (typ === 'email.notify') {
            const to = String(a.params?.to ?? '').trim();
            const sub = String(a.params?.subject ?? '').trim();
            if (!to || !sub) {
              setSnackbar({
                open: true,
                message: 'Email notification: add recipient(s) and subject, or turn off Email.',
                severity: 'error',
              });
              setSaving(false);
              return;
            }
          }
          if (typ === 'genetec.notify' || typ === 'webhook.call') {
            const url = String(a.params?.url ?? '').trim();
            if (!url) {
              setSnackbar({
                open: true,
                message:
                  typ === 'genetec.notify'
                    ? 'Genetec HTTP: HTTPS URL is required, or turn off Genetec.'
                    : 'Webhook: URL is required, or turn off Webhook.',
                severity: 'error',
              });
              setSaving(false);
              return;
            }
          }
        }
      }

      const module_scope = mode === 'ocr' ? 'ocr' : 'warehouse';
      if (editingPolicy) {
        await policiesApi.updatePolicy(siteId, editingPolicy.policy_id, payload, orgId);
        setSnackbar({ open: true, message: 'Policy updated successfully', severity: 'success' });
      } else {
        const created = await policiesApi.createPolicy(siteId, { ...(payload as any), module_scope });
        createdPolicy = created;

        // Smooth flow: create → activate → deploy
        if (effectivePostCreateAction === 'activate' || effectivePostCreateAction === 'activate_deploy') {
          await policiesApi.activatePolicy(created.policy_id, { org_id: orgId, site_id: siteId });
          createdPolicyActivated = true;
        }
        if (effectivePostCreateAction === 'activate_deploy') {
          // Gate 4: auto-arm monitoring for the target device(s) before deploy.
          // This prevents "monitoring_not_armed" from blocking demos after resets/new units.
          if (!siteId) {
            setSnackbar({
              open: true,
              message: 'Deploy blocked: no site selected. Set scope (Incidents → Scope) or add ?site=<site_id> to the URL.',
              severity: 'error',
            });
            handleCloseDialog();
            fetchPolicies();
            fetchZones();
            fetchLines();
            return;
          }
          try {
            const orgId = String((created as any)?.org_id || 'default');
            const deviceIds = deriveDeviceIdsForPolicy(created, {
              cameraId: selectedCameraId,
              zoneIds: selectedZoneIds,
              lineId: selectedLineId,
            });
            const unique = Array.from(new Set((deviceIds || []).map(String).filter(Boolean)));
            if (unique.length > 0) {
              await Promise.all(
                unique.map(async (deviceId) => {
                  // Record a validation PASS. Arming requires latest PASS by default.
                  // If validation fails due to auth/RBAC issues, arming will otherwise fail with
                  // arming_requires_validation_pass; we handle that below.
                  await api.post(`/api/v1/sites/${siteId}/validation/runs`, {
                    org_id: orgId,
                    device_id: deviceId,
                    status: 'pass',
                    details: { source: 'policies_page', policy_id: created.policy_id, camera_id: selectedCameraId },
                  });

                  // Arm monitoring. If it complains about validation pass (race/RBAC/clock skew),
                  // retry with require_validation_pass=false as a demo-safe fallback.
                  try {
                    await api.post(`/api/v1/sites/${siteId}/arming`, {
                      org_id: orgId,
                      device_id: deviceId,
                      monitoring_enabled: true,
                      require_validation_pass: true,
                    });
                  } catch (e: any) {
                    const status = e?.response?.status;
                    const errCode = e?.response?.data?.error;
                    if (status === 409 && errCode === 'arming_requires_validation_pass') {
                      await api.post(`/api/v1/sites/${siteId}/arming`, {
                        org_id: orgId,
                        device_id: deviceId,
                        monitoring_enabled: true,
                        require_validation_pass: false,
                      });
                      setSnackbar({
                        open: true,
                        severity: 'success',
                        message: `Monitoring armed for ${deviceId} (bypassed validation gate).`,
                      });
                      return;
                    }
                    throw e;
                  }
                }),
              );
            }
          } catch (armErr: any) {
            const status = armErr?.response?.status;
            const errCode = armErr?.response?.data?.error;
            const detail = armErr?.response?.data?.detail;
            const msg = (() => {
              if (status === 401) return 'Deploy blocked: not authenticated. Log in or set REACT_APP_CONTROL_PLANE_API_KEY.';
              if (status === 403) return `Deploy blocked: forbidden (${errCode || 'insufficient permissions'}).`;
              if (status === 404) return 'Deploy blocked: arming endpoint not found. Is the cloud API running?';
              if (status === 409 && errCode) return `Deploy blocked: could not arm monitoring (${errCode}).`;
              if (status && errCode) return `Deploy blocked: could not arm monitoring (${status} ${errCode}).`;
              if (armErr?.message?.includes('Network Error')) return 'Deploy blocked: network error. Is the cloud API reachable?';
              const extra = [status, errCode, detail].filter(Boolean).join(' ');
              return extra ? `Deploy blocked: could not arm monitoring (${extra}).` : 'Deploy blocked: could not arm monitoring.';
            })();
            console.error('[Policies] Arming failed:', { status, errCode, detail, err: armErr });
            setSnackbar({ open: true, message: msg, severity: 'error' });
            handleCloseDialog();
            fetchPolicies();
            fetchZones();
            fetchLines();
            return;
          }

          if (isTimeBasedSchedule(created?.schedule)) {
            const deviceIds = deriveDeviceIdsForPolicy(created, {
              cameraId: selectedCameraId,
              zoneIds: selectedZoneIds,
              lineId: selectedLineId,
            });
            const unsynced = deviceIds.filter((id) => clockStatusByDevice[id] === true);
            if (unsynced.length > 0) {
              setClockGatePolicy(created?.name || created?.policy_id || 'Policy');
              setClockGateDevices(unsynced);
              setClockGateOpen(true);
              setSnackbar({
                open: true,
                message: 'Policy created + activated. Deploy blocked due to clock unsynced.',
                severity: 'error',
              });
              handleCloseDialog();
              fetchPolicies();
              fetchZones();
              fetchLines();
              return;
            }
          }
          const dep = await policiesApi.deployPolicy(siteId, created.policy_id);
          const warningCount =
            (Array.isArray(dep?.preflight_warnings) ? dep.preflight_warnings.length : 0) +
            (Array.isArray(dep?.arming_warnings) ? dep.arming_warnings.length : 0);
          setSnackbar({
            open: true,
            message: `Policy deployed (deployment ${dep.deployment_id})${warningCount ? ` with ${warningCount} warning${warningCount === 1 ? '' : 's'}` : ''}.`,
            severity: 'success',
          });
          handleCloseDialog();
          fetchPolicies();
          fetchZones(); // Refresh zones in case they changed
          fetchLines(); // Refresh lines in case they changed
          return;
        }

        setSnackbar({
          open: true,
          message: effectivePostCreateAction === 'activate' ? 'Policy created + activated.' : 'Policy created (draft).',
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchPolicies();
      fetchZones(); // Refresh zones in case they changed
      fetchLines(); // Refresh lines in case they changed
    } catch (err: any) {
      // Special-case: duplicate policy names (409)
      const errCode = err?.response?.data?.error?.code;
      if (err?.response?.status === 409 && errCode === 'policy_name_conflict') {
        const existing = err?.response?.data?.error?.existing_policy_id;
        const msg = existing
          ? `Policy name already exists (conflicts with policy_id=${existing}). Choose a unique name.`
          : 'Policy name already exists. Choose a unique name.';
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return;
      }
      // If create succeeded but auto-deploy was blocked by preflight, treat as partial success.
      const rawError = err?.response?.data?.error;
      const apiErrorCode =
        typeof rawError === 'string' ? rawError : (rawError?.code || '');
      if (createdPolicy && apiErrorCode === 'blocked_by_preflight') {
        const blockedDetails = Array.isArray(err?.response?.data?.details)
          ? err.response.data.details
          : [];
        const blockedDeviceId = String(
          blockedDetails?.[0]?.device_id || ''
        ).trim();
        const msg = createdPolicyActivated
          ? `Policy created + activated. Deploy blocked by preflight${blockedDeviceId ? ` on ${blockedDeviceId}` : ''}.`
          : `Policy created. Deploy blocked by preflight${blockedDeviceId ? ` on ${blockedDeviceId}` : ''}.`;
        setSnackbar({ open: true, message: msg, severity: 'success' });
        handleCloseDialog();
        fetchPolicies();
        fetchZones();
        fetchLines();
        return;
      }
      // Activate-time lint (409): API returns error.issues[] — show same dialog as deploy lint.
      if (err?.response?.status === 409 && apiErrorCode === 'policy_lint_blocked') {
        const issues = Array.isArray(rawError?.issues) ? rawError.issues : [];
        const first = issues.map((i: any) => String(i?.message || i?.kind || '').trim()).filter(Boolean)[0];
        if (issues.length) {
          setDeployLintTitle('Activate blocked: fix policy lint');
          setDeployLintErrors(issues);
          setDeployLintOpen(true);
        }
        const base = createdPolicy
          ? 'Policy was saved as draft but could not be activated.'
          : 'Failed to save policy: Cannot activate policy with unresolved lint errors';
        setSnackbar({
          open: true,
          message: first ? `${base} ${first}` : base,
          severity: 'error',
        });
        if (createdPolicy) {
          handleCloseDialog();
          fetchPolicies();
          fetchZones();
          fetchLines();
        }
        return;
      }
      if (err?.response?.status === 409 && apiErrorCode === 'policy_target_invalid') {
        const detail = rawError?.detail;
        const hint =
          typeof detail === 'string'
            ? detail
            : detail && typeof detail === 'object'
              ? JSON.stringify(detail)
              : '';
        setSnackbar({
          open: true,
          message: createdPolicy
            ? `Policy saved as draft. Target binding invalid: ${hint || 'see API detail'}.`
            : `Cannot activate: invalid target binding. ${hint || ''}`,
          severity: 'error',
        });
        if (createdPolicy) {
          handleCloseDialog();
          fetchPolicies();
          fetchZones();
          fetchLines();
        }
        return;
      }
      if (err?.response?.status === 409 && apiErrorCode === 'zone_reference_missing') {
        const missingZones = (err?.response?.data?.missing_zone_ids || []).filter(Boolean);
        const lintErrors = err?.response?.data?.lint_errors || [];
        const zoneHint = missingZones.length
          ? ` Missing zone(s): ${missingZones.join(', ')}.`
          : '';
        const lintHint = lintErrors.length
          ? ` (${lintErrors.length} lint issue${lintErrors.length === 1 ? '' : 's'})`
          : '';
        if (createdPolicy) {
          const phase = createdPolicyActivated ? 'created + activated' : 'created as draft';
          setSnackbar({
            open: true,
            autoHideDuration: 12_000,
            message: `Policy ${phase}, but deploy failed: zone references are broken.${zoneHint}${lintHint} Re-create or re-enable the missing zone(s) in Zones, then deploy from the policy card.`,
            severity: 'warning',
          });
          handleCloseDialog();
          fetchPolicies();
          fetchZones();
          fetchLines();
        } else {
          setSnackbar({
            open: true,
            autoHideDuration: 10_000,
            message: `Deploy blocked: one or more zone references are missing or disabled.${zoneHint}${lintHint}`,
            severity: 'error',
          });
        }
        return;
      }
      if (createdPolicy) {
        const phase = createdPolicyActivated ? 'created + activated' : 'created as draft';
        const apiMsg =
          err?.response?.data?.error?.message ||
          err?.response?.data?.error?.detail ||
          err?.response?.data?.detail ||
          (typeof err?.response?.data?.error === 'string' ? err.response.data.error : null) ||
          err?.message ||
          'unknown error';
        setSnackbar({
          open: true,
          autoHideDuration: 10_000,
          message: `Policy ${phase}, but a post-create step failed: ${apiMsg}. You can retry deploy from the policy card.`,
          severity: 'warning',
        });
        handleCloseDialog();
        fetchPolicies();
        fetchZones();
        fetchLines();
        return;
      }
      const apiMsg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error?.detail ||
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save policy';
      setSnackbar({ open: true, message: `Failed to save policy: ${apiMsg}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policy: Policy) => {
    if (!window.confirm(`Delete policy "${policy.name}"?`)) return;
    try {
      await policiesApi.deletePolicy(siteId, policy.policy_id, orgId);
      setSnackbar({ open: true, message: 'Policy deleted successfully', severity: 'success' });
      fetchPolicies();
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error?.detail ||
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to delete policy';
      setSnackbar({ open: true, message: `Failed to delete policy: ${apiMsg}`, severity: 'error' });
    }
  };

  const devicesWithPolicies = useMemo(() => listDevicesWithPolicies(policies), [policies]);

  const handleDeleteAllPoliciesForDevice = () =>
    deleteAllPoliciesForDevice({
      deleteAllDeviceId,
      devicesWithPolicies,
      siteId,
      setDeleteAllDeviceSaving,
      setSnackbar,
      setDeleteAllDeviceOpen,
      setDeleteAllDeviceId,
      fetchPolicies,
    });

  const handleDeployAll = useCallback(async () => {
    if (!siteId) {
      setSnackbar({ open: true, message: 'Select a site before deploying policies.', severity: 'error' });
      return;
    }
    try {
      const res = await policiesApi.deployAllPolicies(siteId);
      const warningCount =
        (Array.isArray(res?.preflight_warnings) ? res.preflight_warnings.length : 0) +
        (Array.isArray(res?.arming_warnings) ? res.arming_warnings.length : 0);
      setSnackbar({
        open: true,
        message: res?.deployment_id
          ? `Deployment ${res.deployment_id} started for active policies${warningCount ? ` (${warningCount} warning${warningCount === 1 ? '' : 's'})` : ''}.`
          : 'Policy deployment started.',
        severity: 'success',
      });
      fetchPolicies();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Deploy failed: ${formatPolicyApiError(err, 'Failed to deploy policies')}`,
        severity: 'error',
      });
    }
  }, [fetchPolicies, siteId]);

  const openCreatePolicy = () =>
    packFilter !== 'all' ? navigate(`/policies?create=1&pack=${encodeURIComponent(packFilter)}`) : handleOpenDialog();

  const openCreatePolicyPack = handleOpenDialog;

  const openMeasureForPolicy = useCallback((policy: Policy) => {
    const params = new URLSearchParams();
    params.set('policy_type', String(policy.policy_type || '').trim());
    const cameraId = String(
      policy.camera_id
      || policy.target?.camera_id
      || policy.scope?.camera_ids?.[0]
      || '',
    ).trim();
    const deviceId = String(
      policy.edge_device_id
      || policy.target?.edge_device_id
      || policy.scope?.edge_device_id
      || '',
    ).trim();
    if (cameraId) params.set('camera', cameraId);
    if (deviceId) params.set('device', deviceId);
    params.set('policy_id', String(policy.policy_id || '').trim());
    navigate(`/accuracy?${params.toString()}`);
  }, [navigate]);

  const policyMenuRow = useMemo(
    () => filteredPolicyRows.find((row) => row.id === policyMenuRowId) || allPolicyRows.find((row) => row.id === policyMenuRowId) || null,
    [allPolicyRows, filteredPolicyRows, policyMenuRowId],
  );

  const openOverrideDialog = (policy: Policy) => {
    setOverridePolicy(policy);
    setOverrideDisable(false);
    setOverrideSeverity(((policy as any)?.alert?.severity as any) || '');
    setOverrideCooldownSeconds('');
    setOverrideDurationMinutes(120);
    setOverrideReason('maintenance');
    setOverrideOpen(true);
  };

  const submitOverride = async () => {
    if (!overridePolicy) return;
    setOverrideSaving(true);
    try {
      const overrides: any = {};
      if (overrideDisable) overrides.enabled = false;
      if (overrideSeverity) overrides.severity = overrideSeverity;
      if (overrideCooldownSeconds.trim()) overrides.cooldown_seconds = Number(overrideCooldownSeconds.trim());

      const durSec = Math.max(60, Math.floor(Number(overrideDurationMinutes || 0) * 60));
      const resp = await operatorOverridesApi.create(siteId, {
        scope: { policy_id: overridePolicy.policy_id },
        overrides,
        duration_seconds: durSec,
        reason: overrideReason || 'operator_override',
      });
      const depId = resp?.deploy?.deployment_id;
      setSnackbar({
        open: true,
        severity: 'success',
        message: depId ? `Override applied (deployment ${depId}).` : 'Override applied.',
      });
      setOverrideOpen(false);
      // Refresh policy list for latest deployment_state
      void fetchPolicies();
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to apply override',
      });
    } finally {
      setOverrideSaving(false);
    }
  };

  const closeZonePreviewDialog = useCallback(() => {
    setZonePreviewOpen(false);
    setZonePreviewZone(null);
    setZonePreviewImg(null);
    setZonePreviewError(null);
    setZonePreviewLoading(false);
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const openClockPreflight = useCallback(() => {
    const target = clockGateDevices[0] || '';
    navigate(target ? `/preflight?device=${encodeURIComponent(target)}` : '/preflight');
    setClockGateOpen(false);
  }, [clockGateDevices, navigate]);

  const openLintFix = useCallback((href: string) => {
    navigate(href);
    setDeployLintOpen(false);
  }, [navigate]);

  const startMeasuring = useStartMeasuring({
    siteId,
    orgId,
    policy: selectedPolicyView?.policy || null,
    enabled: Boolean(selectedPolicyView),
    navigate,
  });

  return (
    <PoliciesPageContent
      PoliciesIntroSectionComponent={PoliciesIntroSection}
      PoliciesPackSectionsComponent={PoliciesPackSections}
      PoliciesEditorBodyComponent={PoliciesEditorBody}
      qReturnTo={qReturnTo}
      canGoBack={qReturnTo === 'setup' || window.history.length > 1}
      handleBackFromPolicies={handleBackFromPolicies}
      canWritePolicies={canWritePolicies}
      canDeployPolicies={canDeployPoliciesFlag}
      openCreatePolicy={openCreatePolicy}
      openCreatePolicyPack={openCreatePolicyPack}
      handleDeployAll={handleDeployAll}
      setPageMenuAnchor={setPageMenuAnchor}
      pageMenuAnchor={pageMenuAnchor}
      packSummaries={packSummaries}
      packFilter={packFilter}
      setPackFilter={setPackFilter}
      policySummary={policySummary}
      qPack={qPack}
      qType={qType}
      qCameraId={qCameraId}
      qZoneId={qZoneId}
      qLineId={qLineId}
      clearFilters={clearFilters}
      getPolicyTypeLabel={getPolicyTypeLabel}
      siteComplexity={siteComplexity}
      canReadPolicies={canReadPolicies}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      policiesLoaded={policiesLoaded}
      policyRows={policyRows}
      loading={loading}
      groupedPolicyViews={groupedPolicyViews}
      filteredPolicyRows={filteredPolicyRows}
      selectedPolicyView={selectedPolicyView}
      setPolicyDetailsId={setPolicyDetailsId}
      selectedPolicyPresented={selectedPolicyPresented}
      zonesById={zonesById}
      canaryGroup={canaryGroup}
      scopeCanary={scopeCanary}
      scopeFull={scopeFull}
      navigate={navigate}
      handleOpenDialog={handleOpenDialog}
      openOverrideDialog={openOverrideDialog}
      handleActivate={handleActivate}
      handleDeactivate={handleDeactivate}
      handleDeploy={handleDeploy}
      handleDelete={handleDelete}
      openMeasureForPolicy={openMeasureForPolicy}
      measurement={startMeasuring.presentation}
      measurementBusy={startMeasuring.busy}
      measurementError={startMeasuring.error}
      onStartMeasuring={() => void startMeasuring.startMeasuring()}
      onSaveForLater={() => setPolicyDetailsId('')}
      fetchPolicies={fetchPolicies}
      fetchZones={fetchZones}
      fetchCameras={fetchCameras}
      devicesWithPolicies={devicesWithPolicies}
      setDeleteAllDeviceOpen={setDeleteAllDeviceOpen}
      policyMenuAnchor={policyMenuAnchor}
      setPolicyMenuAnchor={setPolicyMenuAnchor}
      policyMenuRow={policyMenuRow}
      policyMenuRowId={policyMenuRowId}
      setPolicyMenuRowId={setPolicyMenuRowId}
      dialogOpen={dialogOpen}
      handleCloseDialog={handleCloseDialog}
      editingPolicy={editingPolicy}
      editorStep={editorStep}
      setEditorStep={setEditorStep}
      selectedPolicyPack={selectedPolicyPack}
      selectionReady={selectionReady}
      scopeReady={scopeReady}
      saving={saving}
      canSavePolicy={canSavePolicy}
      primarySaveLabel={primarySaveLabel}
      handleSave={handleSave}
      selectedPolicyAlias={selectedPolicyAlias}
      setSelectedPolicyAlias={setSelectedPolicyAlias}
      setSelectedPolicyPack={setSelectedPolicyPack}
      applyPolicyAliasSelection={applyPolicyAliasSelection}
      selectedPolicyOption={selectedPolicyOption}
      selectedPolicySubject={selectedPolicySubject}
      formData={formData}
      setFormData={setFormData}
      selectedCameraId={selectedCameraId}
      setSelectedCameraId={setSelectedCameraId}
      selectedCameraProfileId={selectedCameraProfileId}
      selectedEdgeDeviceId={selectedEdgeDeviceId}
      setSelectedEdgeDeviceId={setSelectedEdgeDeviceId}
      cameras={cameras}
      selectedZoneIds={selectedZoneIds}
      setSelectedZoneIds={setSelectedZoneIds}
      selectedLineId={selectedLineId}
      setSelectedLineId={setSelectedLineId}
      lineDirection={lineDirection}
      setLineDirection={setLineDirection}
      availableZones={availableZones}
      availableLines={availableLines}
      openZonePreview={openZonePreview}
      scopeRequirements={scopeRequirements}
      editorTab={editorTab}
      setEditorTab={setEditorTab}
      weekGrid={weekGrid}
      setWeekGrid={setWeekGrid}
      initDefaultGrid={_initDefaultGrid}
      gridToWindows={_gridToWindows}
      mode={mode}
      clockStatusByDevice={clockStatusByDevice}
      deriveDeviceIdsForPolicy={deriveDeviceIdsForPolicy}
      setSnackbar={setSnackbar}
      selectedPackGroup={selectedPackGroup}
      allowsWholeViewScope={allowsWholeViewScope}
      postCreateAction={postCreateAction}
      setPostCreateAction={setPostCreateAction} sitePolicies={policies}
      overrideOpen={overrideOpen}
      setOverrideOpen={setOverrideOpen}
      overridePolicy={overridePolicy}
      overrideDisable={overrideDisable}
      setOverrideDisable={setOverrideDisable}
      overrideSeverity={overrideSeverity}
      setOverrideSeverity={setOverrideSeverity}
      overrideCooldownSeconds={overrideCooldownSeconds}
      setOverrideCooldownSeconds={setOverrideCooldownSeconds}
      overrideDurationMinutes={overrideDurationMinutes}
      setOverrideDurationMinutes={setOverrideDurationMinutes}
      overrideReason={overrideReason}
      setOverrideReason={setOverrideReason}
      overrideSaving={overrideSaving}
      severityOptions={SEVERITIES}
      submitOverride={submitOverride}
      deleteAllDeviceOpen={deleteAllDeviceOpen}
      setDeleteAllDeviceOpenLocal={setDeleteAllDeviceOpen}
      deleteAllDeviceSaving={deleteAllDeviceSaving}
      deleteAllDeviceId={deleteAllDeviceId}
      setDeleteAllDeviceId={setDeleteAllDeviceId}
      handleDeleteAllPoliciesForDevice={handleDeleteAllPoliciesForDevice}
      zonePreviewOpen={zonePreviewOpen}
      closeZonePreviewDialog={closeZonePreviewDialog}
      zonePreviewZone={zonePreviewZone}
      zonePreviewImg={zonePreviewImg}
      zonePreviewLoading={zonePreviewLoading}
      zonePreviewError={zonePreviewError}
      zoneOverlay={_zoneOverlay}
      deployLintOpen={deployLintOpen}
      setDeployLintOpen={setDeployLintOpen}
      deployLintTitle={deployLintTitle}
      deployLintErrors={deployLintErrors}
      lintFixHref={lintFixHref}
      openLintFix={openLintFix}
      clockGateOpen={clockGateOpen}
      setClockGateOpen={setClockGateOpen}
      clockGatePolicy={clockGatePolicy}
      clockGateDevices={clockGateDevices}
      openClockPreflight={openClockPreflight}
      snackbar={snackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default Policies;
